import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5QrcodeScanner } from 'html5-qrcode';
import { Button } from '../components/shared/Button';
import { useSpotify } from '../context/SpotifyContext';
import { SpotifyPlayer, SpotifyPlayButton } from '../components/Spotify/SpotifyPlayer';
import { DiscogsVerify } from '../components/Discogs';
import { SongTools } from '../components/SongTools';
import { PlaylistWizard } from '../components/PlaylistWizard';
import { LyricsModal } from '../components/LyricsModal';
import { HitsterEvent, EventRound, EventSong, HitsterGenerationPeriodId, HitsterRoundVariant } from '../types';
import './SpotifyDashboard.css';

type GenerationFilterValue = 'all' | HitsterGenerationPeriodId;

// YouTube IFrame Player type
interface YTPlayer {
  getCurrentTime: () => number;
  seekTo: (seconds: number, allowSeekAhead?: boolean) => void;
  playVideo: () => void;
  pauseVideo: () => void;
  destroy: () => void;
}
const API_BASE = process.env.REACT_APP_API_URL || '';
interface SpotifyTrack {
  id: string;
  title: string;
  artist: string;
  album: string;
  year: number | null;
  duration: number;
  thumbnail: string | null;
  previewUrl: string | null;
  spotifyUrl: string;
  recommendedGain?: number;
}
interface YouTubeResult {
  id: string;
  title: string;
  channel: string;
  duration: number;
  thumbnail: string;
  url: string;
}
interface YouTubeVideoInfo {
  id: string;
  title: string;
  artist: string;
  channel: string;
  duration: number;
  thumbnail: string;
  year: number;
  sourceUrl?: string;
}
interface DownloadedFile {
  filename: string;
  url: string;
  size: number;
  title?: string;
  artist?: string;
  year?: number;
}
interface DisplayClientStatus {
  lastSeen?: number;
  spotifyAuthenticated?: boolean;
  spotifyPremium?: boolean;
  spotifyReady?: boolean;
  playbackConfirmed?: boolean;
  currentSongId?: string | null;
  actualSpotifyUri?: string | null;
  error?: string | null;
}
// Decades for quick filtering
const DECADES = [
  { label: '60s', start: 1960, end: 1969 },
  { label: '70s', start: 1970, end: 1979 },
  { label: '80s', start: 1980, end: 1989 },
  { label: '90s', start: 1990, end: 1999 },
  { label: '00s', start: 2000, end: 2009 },
  { label: '10s', start: 2010, end: 2019 },
  { label: '20s', start: 2020, end: 2029 },
];

const HITSTER_ROUND_VARIANTS: Array<{ id: HitsterRoundVariant; label: string; description: string }> = [
  {
    id: 'classic',
    label: 'Classic Hitster',
    description: 'Standaard Hitster zonder generatiekeuze per beurt.'
  },
  {
    id: 'battle-generations',
    label: 'Battle of the Generations',
    description: 'Kies per beurt handmatig welke generatie actief is.'
  }
];

const HITSTER_GENERATION_PERIODS: Array<{
  id: HitsterGenerationPeriodId;
  label: string;
  yearLabel: string;
  minYear: number;
  maxYear: number;
  iconUrl?: string;
}> = [
  {
    id: 'vinyl-classics',
    label: 'Vinyl Classics',
    yearLabel: '1984 en eerder',
    minYear: Number.NEGATIVE_INFINITY,
    maxYear: 1984,
    iconUrl: '/assets/battle-generations/vinyl.png'
  },
  {
    id: 'cassette-cd',
    label: 'Cassette & CD',
    yearLabel: '1985 - 2004',
    minYear: 1985,
    maxYear: 2004,
    iconUrl: '/assets/battle-generations/cassette-cd.png'
  },
  {
    id: 'streaming',
    label: 'The Streaming',
    yearLabel: '2005+',
    minYear: 2005,
    maxYear: Number.POSITIVE_INFINITY
  }
];

const getRoundVariantLabel = (variant?: HitsterRoundVariant): string => {
  return HITSTER_ROUND_VARIANTS.find((item) => item.id === (variant || 'classic'))?.label || 'Classic Hitster';
};

const getEventType = (event?: HitsterEvent | null): 'rounds' | 'theme-night' => {
  return event?.eventType === 'theme-night' ? 'theme-night' : 'rounds';
};

const THEME_NIGHT_ROUND_ID = 'theme-night';

const getThemeNightSetNumberFromId = (setId?: string | null): number | null => {
  if (!setId) return null;
  const match = /^theme-night-set-(\d+)$/.exec(setId.trim());
  if (!match) return null;
  const parsed = Number(match[1]);
  // Ignore old timestamp-like ids and invalid values.
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 999) return null;
  return parsed;
};

const getEventSongCount = (event?: HitsterEvent | null): number => {
  if (!event) return 0;
  if (getEventType(event) === 'theme-night') {
    return (event.themeSongs || []).length;
  }
  return (event.rounds || []).reduce((sum, round) => sum + (round.songs || []).length, 0);
};

const getGenerationPeriodForYear = (year?: number | string | null) => {
  const parsedYear = typeof year === 'number'
    ? year
    : typeof year === 'string'
      ? Number(year.trim())
      : NaN;

  if (!Number.isFinite(parsedYear)) {
    return null;
  }

  return HITSTER_GENERATION_PERIODS.find((period) => parsedYear >= period.minYear && parsedYear <= period.maxYear) || null;
};

const GENERATION_FILTER_OPTIONS: Array<{ id: GenerationFilterValue; label: string }> = [
  { id: 'all', label: 'Alles' },
  { id: 'vinyl-classics', label: 'Vinyl' },
  { id: 'cassette-cd', label: 'Cassette/CD' },
  { id: 'streaming', label: 'Streaming' },
];

const filterSongsByGeneration = (songs: EventSong[], filterValue: GenerationFilterValue): EventSong[] => {
  if (filterValue === 'all') {
    return songs;
  }
  return songs.filter((song) => getGenerationPeriodForYear(song.year)?.id === filterValue);
};

const SpotifyDashboard: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isPremium, isReady, play, pause, resume, seek, setVolume, player } = useSpotify();
  
  // Check admin authentication
  useEffect(() => {
    const isAdmin = localStorage.getItem('hitster-admin') || sessionStorage.getItem('hitster-admin');
    if (!isAdmin) {
      navigate('/');
    }
  }, [navigate]);
  
  // Fade state
  const [isFading, setIsFading] = useState(false);
  const [fadeDurationSeconds, setFadeDurationSeconds] = useState<number>(() => {
    const saved = localStorage.getItem('hitster-fade-duration-seconds');
    const parsed = saved ? Number(saved) : NaN;
    if (Number.isFinite(parsed) && parsed >= 1 && parsed <= 10) return parsed;
    return 3;
  });
  const [isYoutubePaused, setIsYoutubePaused] = useState(false);
  const [isIntermissionActive, setIsIntermissionActive] = useState(false);
  const [isScoreboardActive, setIsScoreboardActive] = useState(false);
  const [isRoundAnswersActive, setIsRoundAnswersActive] = useState(false);
  // Events state
  const [events, setEvents] = useState<HitsterEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<HitsterEvent | null>(null);
  const [selectedRound, setSelectedRound] = useState<EventRound | null>(null);
  const [showEventModal, setShowEventModal] = useState(false);
  const [newEventName, setNewEventName] = useState('');
  const [newEventDate, setNewEventDate] = useState('');
  const [newEventDescription, setNewEventDescription] = useState('');
  const [newEventLogoUrl, setNewEventLogoUrl] = useState('');
  const [newEventIntermissionMessage, setNewEventIntermissionMessage] = useState('');
  // Event editing
  const [editingEvent, setEditingEvent] = useState<HitsterEvent | null>(null);
  const [editEventName, setEditEventName] = useState('');
  const [editEventLogoUrl, setEditEventLogoUrl] = useState('');
  const [editEventIntermissionMessage, setEditEventIntermissionMessage] = useState('');
  // Round creation
  const [showRoundModal, setShowRoundModal] = useState(false);
  const [newRoundName, setNewRoundName] = useState('');
  const [newRoundVariant, setNewRoundVariant] = useState<HitsterRoundVariant>('classic');
  
  // Playlist wizard
  const [showPlaylistWizard, setShowPlaylistWizard] = useState(false);
  // Spotify config
  const [isConfigured, setIsConfigured] = useState<boolean | null>(null);
  const [showConfig, setShowConfig] = useState(false);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  // Search state
  const [searchMode, setSearchMode] = useState<'spotify' | 'youtube' | 'downloads'>('spotify');
  const [searchQuery, setSearchQuery] = useState('');
  const [artistFilter, setArtistFilter] = useState('');
  const [trackFilter, setTrackFilter] = useState('');
  const [albumFilter, setAlbumFilter] = useState('');
  const [yearFrom, setYearFrom] = useState('');
  const [yearTo, setYearTo] = useState('');
  const [showSpotifyResultsModal, setShowSpotifyResultsModal] = useState(false);
  const [selectedDecade, setSelectedDecade] = useState<string | null>(null);
  const [genreFilter, setGenreFilter] = useState('');
  const [onlyWithPreview, setOnlyWithPreview] = useState(false);
  const [searchLimit, setSearchLimit] = useState(25);
  const [searchResults, setSearchResults] = useState<SpotifyTrack[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // YouTube search state
  const [ytSearchQuery, setYtSearchQuery] = useState('');
  const [ytResults, setYtResults] = useState<YouTubeResult[]>([]);
  const [ytSelectedVideo, setYtSelectedVideo] = useState<YouTubeVideoInfo | null>(null);
  const [ytPreviewingId, setYtPreviewingId] = useState<string | null>(null);
  const [ytIsSearching, setYtIsSearching] = useState(false);
  const [ytIsDownloading, setYtIsDownloading] = useState(false);
  const [ytIsLoadingInfo, setYtIsLoadingInfo] = useState(false);
  const [ytDownloadStatus, setYtDownloadStatus] = useState('');
  const [ytEditTitle, setYtEditTitle] = useState('');
  const [ytEditArtist, setYtEditArtist] = useState('');
  const [ytEditYear, setYtEditYear] = useState('');
  // Downloaded files state
  const [downloadedFiles, setDownloadedFiles] = useState<DownloadedFile[]>([]);
  const [downloadsLoading, setDownloadsLoading] = useState(false);
  const [editingDownload, setEditingDownload] = useState<DownloadedFile | null>(null);
  const [dlEditTitle, setDlEditTitle] = useState('');
  const [dlEditArtist, setDlEditArtist] = useState('');
  // Lyrics modal state
  const [lyricsTrack, setLyricsTrack] = useState<{ artist: string; title: string } | null>(null);
  const [dlEditYear, setDlEditYear] = useState('');
  const [playingDownloadUrl, setPlayingDownloadUrl] = useState<string | null>(null);
  const downloadAudioRef = useRef<HTMLAudioElement | null>(null);
  // QR Scanner state
  const [showQrScanner, setShowQrScanner] = useState(false);
  const [qrScannerError, setQrScannerError] = useState<string | null>(null);
  const [qrProcessing, setQrProcessing] = useState(false);
  const qrScannerRef = useRef<Html5QrcodeScanner | null>(null);
  // Round songs display state
  const [roundSongsCollapsed, setRoundSongsCollapsed] = useState(false);
  const [eventsCollapsed, setEventsCollapsed] = useState(true);
  const [roundsCollapsed, setRoundsCollapsed] = useState(true);
  const [roundViewTab, setRoundViewTab] = useState<'songs' | 'discogs' | 'tools'>('songs');
  const [rightPanelTab, setRightPanelTab] = useState<'nummers' | 'zoeken'>('nummers');
  const [playingSongId, setPlayingSongId] = useState<string | null>(null);
  const [playingYouTubeId, setPlayingYouTubeId] = useState<string | null>(null);
  const [draggedSongId, setDraggedSongId] = useState<string | null>(null);
  const [dropTargetSongId, setDropTargetSongId] = useState<string | null>(null);
  const [movingSongId, setMovingSongId] = useState<string | null>(null);
  const [liveSongId, setLiveSongId] = useState<string | null>(null);
  const [armedDisplaySongId, setArmedDisplaySongId] = useState<string | null>(null);
  const [themeNightSetSongIds, setThemeNightSetSongIds] = useState<string[]>([]);
  const [themeNightSetStatus, setThemeNightSetStatus] = useState<string | null>(null);
  const [themeNightSetSaving, setThemeNightSetSaving] = useState(false);
  const [themeNightSetHistory, setThemeNightSetHistory] = useState<Array<{ setId: string; count: number; lastPlayedAt: string | null }>>([]);
  const [selectedThemeNightSetId, setSelectedThemeNightSetId] = useState<string | null>(null);
  const [themeNightSongSetMap, setThemeNightSongSetMap] = useState<Record<string, number[]>>({});
  const [themeNightOverviewSetFilter, setThemeNightOverviewSetFilter] = useState<string>('all');
  const [themeNightPanelSetId, setThemeNightPanelSetId] = useState<string | null>(null);
  const [themeNightPanelFocus, setThemeNightPanelFocus] = useState<'answers' | 'teams'>('answers');
  const [themeNightPanelAnswerKeys, setThemeNightPanelAnswerKeys] = useState<Array<{ songId: string; categoryId: string | null; playedAt: number }>>([]);
  const [themeNightPanelBingoScores, setThemeNightPanelBingoScores] = useState<Record<string, number>>({});
  const [themeNightPanelLoading, setThemeNightPanelLoading] = useState(false);
  const [showSongOverview, setShowSongOverview] = useState(false);
  const [overviewTab, setOverviewTab] = useState<'table' | 'discogs' | 'tools'>('table');
  const [overviewGenerationFilter, setOverviewGenerationFilter] = useState<GenerationFilterValue>('all');
  const [rightPanelGenerationFilter, setRightPanelGenerationFilter] = useState<GenerationFilterValue>('all');
  const [overviewPlayingSongId, setOverviewPlayingSongId] = useState<string | null>(null);
  const [isPlayingAllRounds, setIsPlayingAllRounds] = useState(false);
  const [playAllStatus, setPlayAllStatus] = useState<string | null>(null);
  const overviewAudioRef = useRef<HTMLAudioElement | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const spotifyGainCacheRef = useRef<Record<string, number>>({});
  const playAllTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const playAllActiveRef = useRef(false);
  const displayActionInFlightRef = useRef(false);
  const liveSongIdRef = useRef<string | null>(null);
  const lastDisplayActionRef = useRef<{ songId: string | null; startedAt: number }>({
    songId: null,
    startedAt: 0
  });
  const queuedDisplaySelectionRef = useRef<{
    songId: string;
    songOrder: number;
    roundNumber: number;
    roundName: string;
    hitsterGenerationPeriodId?: HitsterGenerationPeriodId | null;
    hitsterGenerationPeriodLabel?: string | null;
    hitsterGenerationPeriodIconUrl?: string | null;
  } | null>(null);
  const liveSongOrderRef = useRef<number | null>(null);
  const liveRoundSongCountRef = useRef<number | null>(null);
  const liveRoundOrderRef = useRef<number | null>(null);
  const [startingDisplaySongId, setStartingDisplaySongId] = useState<string | null>(null);
  const [selectedGenerationPeriodId, setSelectedGenerationPeriodId] = useState<HitsterGenerationPeriodId>('vinyl-classics');
  // Inline editing state for overview table
  const [editingOverviewSongId, setEditingOverviewSongId] = useState<string | null>(null);
  const [editOverviewTitle, setEditOverviewTitle] = useState('');
  const [editOverviewArtist, setEditOverviewArtist] = useState('');
  const [editOverviewYear, setEditOverviewYear] = useState('');
  const [editOverviewYearSource, setEditOverviewYearSource] = useState('');
  // Lyrics editing state
  const [editingLyricsSongId, setEditingLyricsSongId] = useState<string | null>(null);
  const [editingLyricsSong, setEditingLyricsSong] = useState<EventSong | null>(null);
  const [lyricsText, setLyricsText] = useState('');
  const [lyricsPlaybackPosition, setLyricsPlaybackPosition] = useState(0);
  const [lyricsIsPlaying, setLyricsIsPlaying] = useState(false);
  const [lyricsSearching, setLyricsSearching] = useState(false);
  const [lyricsSaved, setLyricsSaved] = useState(false);
  const lyricsAudioRef = useRef<HTMLAudioElement | null>(null);
  const lyricsTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const lyricsCursorRef = useRef<number | null>(null);
  const [lyricsPreviewMode, setLyricsPreviewMode] = useState(false);
  const activeLyricsLineRef = useRef<HTMLDivElement | null>(null);
  const lyricsIsPlayingRef = useRef(false);
  const insertTimestampRef = useRef<() => void>(() => {});
  const toggleLyricsPlaybackRef = useRef<() => void>(() => {});
  // YouTube timing editing state
  const [editingYoutubeTimingSongId, setEditingYoutubeTimingSongId] = useState<string | null>(null);
  const [editingYoutubeTimingSong, setEditingYoutubeTimingSong] = useState<EventSong | null>(null);
  const [youtubeStartTimeInput, setYoutubeStartTimeInput] = useState('');
  const [youtubeEndTimeInput, setYoutubeEndTimeInput] = useState('');
  const [audioStartTimeInput, setAudioStartTimeInput] = useState('');
  const [volumeGainInput, setVolumeGainInput] = useState(100); // percentage 10-200
  const ytTimingPlayerRef = useRef<YTPlayer | null>(null);
  const [ytTimingApiReady, setYtTimingApiReady] = useState(false);

  // Load YouTube IFrame API for timing editor
  useEffect(() => {
    if (window.YT && window.YT.Player) {
      setYtTimingApiReady(true);
      return;
    }
    
    // Check if script is already loading
    if (document.querySelector('script[src*="youtube.com/iframe_api"]')) {
      const checkReady = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setYtTimingApiReady(true);
          clearInterval(checkReady);
        }
      }, 100);
      return () => clearInterval(checkReady);
    }
    
    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);
    
    window.onYouTubeIframeAPIReady = () => {
      setYtTimingApiReady(true);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('hitster-fade-duration-seconds', String(fadeDurationSeconds));
  }, [fadeDurationSeconds]);

  useEffect(() => {
    liveSongIdRef.current = liveSongId;
  }, [liveSongId]);

  // Poll display state while a song is live so the dashboard auto-resets
  // when the display calls /stop (e.g. song ended on the display screen)
  useEffect(() => {
    if (!liveSongId) return;
    const interval = setInterval(async () => {
      try {
        const response = await fetch(`${API_BASE}/api/display-state`);
        if (!response.ok) return;
        const data = await response.json();
        // If the server says nothing is playing while we think something is live, sync up
        if (!data.isPlaying && liveSongIdRef.current) {
          applyDisplayStateSnapshot(data);
        }
      } catch {
        // Ignore network errors
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [liveSongId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Initialize YouTube player when modal opens
  useEffect(() => {
    if (!editingYoutubeTimingSong?.youtubeVideoId || !ytTimingApiReady) return;
    
    // Wait for DOM to be ready
    const initPlayer = setTimeout(() => {
      const container = document.getElementById('yt-timing-player');
      if (!container) return;
      
      // Destroy existing player
      if (ytTimingPlayerRef.current) {
        ytTimingPlayerRef.current.destroy();
        ytTimingPlayerRef.current = null;
      }
      
      try {
        ytTimingPlayerRef.current = new window.YT.Player('yt-timing-player', {
          videoId: editingYoutubeTimingSong.youtubeVideoId,
          playerVars: {
            autoplay: 0,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            start: parseTimeToSeconds(youtubeStartTimeInput) || 0
          },
          events: {
            onReady: () => {
              // Player is ready
              console.log('YouTube timing player ready');
            }
          }
        });
      } catch (err) {
        console.error('Failed to create YouTube player:', err);
      }
    }, 100);
    
    return () => {
      clearTimeout(initPlayer);
      if (ytTimingPlayerRef.current) {
        ytTimingPlayerRef.current.destroy();
        ytTimingPlayerRef.current = null;
      }
    };
  }, [editingYoutubeTimingSong?.youtubeVideoId, ytTimingApiReady]);

  // Poll Spotify position while editing lyrics
  useEffect(() => {
    if (!lyricsIsPlaying || !editingLyricsSong?.spotifyUri || !player) return;
    const pollInterval = setInterval(async () => {
      try {
        const state = await player.getCurrentState();
        if (state && state.position !== undefined) {
          setLyricsPlaybackPosition(state.position);
        }
      } catch (err) {
        console.error('Error polling Spotify position:', err);
      }
    }, 100); // Poll every 100ms for smooth updates
    return () => clearInterval(pollInterval);
  }, [lyricsIsPlaying, editingLyricsSong?.spotifyUri, player]);

  // Apply cursor position after lyricsText state update (insertTimestamp)
  useEffect(() => {
    if (lyricsCursorRef.current !== null && lyricsTextareaRef.current) {
      const pos = lyricsCursorRef.current;
      lyricsCursorRef.current = null;
      lyricsTextareaRef.current.setSelectionRange(pos, pos);
      lyricsTextareaRef.current.focus();
    }
  }, [lyricsText]);

  // Keep lyricsIsPlayingRef in sync
  useEffect(() => { lyricsIsPlayingRef.current = lyricsIsPlaying; }, [lyricsIsPlaying]);

  // Scroll active preview line into view
  useEffect(() => {
    if (lyricsPreviewMode) {
      activeLyricsLineRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [lyricsPlaybackPosition, lyricsPreviewMode]);

  // Keyboard shortcut: Spacebar inserts timestamp (when lyrics modal is open and not typing)
  useEffect(() => {
    if (!editingLyricsSongId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code !== 'Space') return;
      const inTextarea = document.activeElement === lyricsTextareaRef.current;
      if (inTextarea) {
        // In textarea: alleen spatie onderscheppen als muziek speelt (sync-modus)
        if (lyricsIsPlayingRef.current) {
          e.preventDefault();
          insertTimestampRef.current();
        }
        // anders: gewone spatie typen toestaan
      } else {
        e.preventDefault();
        if (lyricsIsPlayingRef.current) {
          insertTimestampRef.current();
        } else {
          toggleLyricsPlaybackRef.current();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingLyricsSongId]);

  // ─── Playback log ────────────────────────────────────────────────────────
  interface LogEntry {
    id: string;
    ts: number;
    source: string;
    level: 'info' | 'warn' | 'error' | 'ok';
    event: string;
    data?: any;
  }
  const [playbackLogEntries, setPlaybackLogEntries] = useState<LogEntry[]>([]);
  const [showPlaybackLog, setShowPlaybackLog] = useState(false);
  const lastLogTsRef = useRef<number>(0);

  const pushLog = useCallback(async (
    level: LogEntry['level'],
    event: string,
    data?: any
  ) => {
    try {
      await fetch(`${API_BASE}/api/playback-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'dashboard', level, event, data: data ?? null })
      });
    } catch (_) { /* ignore */ }
  }, []);

  // Poll log when panel is open
  useEffect(() => {
    if (!showPlaybackLog) return;
    const poll = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/playback-log?since=${lastLogTsRef.current}`);
        if (!res.ok) return;
        const entries: LogEntry[] = await res.json();
        if (entries.length > 0) {
          lastLogTsRef.current = entries[0].ts;
          setPlaybackLogEntries(prev => {
            const merged = [...entries, ...prev];
            return merged.slice(0, 200);
          });
        }
      } catch (_) { /* ignore */ }
    };
    poll();
    const interval = setInterval(poll, 1000);
    return () => clearInterval(interval);
  }, [showPlaybackLog]);

  const clearPlaybackLog = async () => {
    try {
      await fetch(`${API_BASE}/api/playback-log`, { method: 'DELETE' });
      setPlaybackLogEntries([]);
      lastLogTsRef.current = 0;
    } catch (_) { /* ignore */ }
  };
  // ─────────────────────────────────────────────────────────────────────────

  // Load events on mount
  useEffect(() => {
    loadEvents();
    checkSpotifyStatus();
    loadDisplayState();
  }, []);

  const applyDisplayStateSnapshot = (data: any) => {
    const liveId = data.isPlaying && data.currentSong?.id ? data.currentSong.id : null;

    setLiveSongId(liveId);
    liveSongIdRef.current = liveId;
    liveSongOrderRef.current = data.currentSongOrder ?? data.currentSong?.order ?? null;
    liveRoundOrderRef.current = data.roundNumber ?? null;
    setIsYoutubePaused(Boolean(data.youtubePaused));
    setIsIntermissionActive(Boolean(data.isIntermission));
    setIsScoreboardActive(Boolean(data.showScoreboard));
    setIsRoundAnswersActive(Boolean(data.showRoundAnswers));

    if (data.mode === 'rules' || !liveId) {
      queuedDisplaySelectionRef.current = null;
      setArmedDisplaySongId(null);
    }
  };

  const loadDisplayState = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/display-state`);
      if (!response.ok) return;

      const data = await response.json();
      applyDisplayStateSnapshot(data);
    } catch (err) {
      console.warn('Could not load display state:', err);
    }
  };

  const getDisplayClientStatus = async () => {
    const response = await fetch(`${API_BASE}/api/display-state`);
    if (!response.ok) {
      throw new Error('De status van het display-scherm kon niet worden gecontroleerd.');
    }

    const data = await response.json();
    return {
      data,
      displayClient: (data.displayClient ?? {}) as DisplayClientStatus,
      checkedAt: Date.now()
    };
  };
  const checkSpotifyStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/spotify/status`);
      const data = await response.json();
      setIsConfigured(data.configured);
    } catch (err) {
      setIsConfigured(false);
    }
  };
  const handleConfigure = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/spotify/configure`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ clientId, clientSecret }),
      });
      
      if (response.ok) {
        setIsConfigured(true);
        setShowConfig(false);
        setError(null);
      } else {
        setError('Configuratie mislukt');
      }
    } catch (err) {
      setError('Kon niet verbinden met server');
    }
  };
  const loadEvents = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/events`);
      if (response.ok) {
        const rawData = await response.json();
        const data: HitsterEvent[] = (rawData || []).map((event: HitsterEvent) => ({
          ...event,
          rounds: event.rounds || [],
          themeSongs: event.themeSongs || [],
        }));
        setEvents(data);
      }
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };
  const createEvent = async () => {
    if (!newEventName.trim()) return;
    const event: HitsterEvent = {
      id: `event-${Date.now()}`,
      name: newEventName,
      date: newEventDate || new Date().toISOString().split('T')[0],
      description: newEventDescription,
      logoUrl: newEventLogoUrl || undefined,
      intermissionMessage: newEventIntermissionMessage.trim() || undefined,
      rounds: [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    try {
      const response = await fetch(`${API_BASE}/api/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event),
      });
      if (response.ok) {
        const savedEvent = await response.json();
        setEvents([...events, savedEvent]);
        setSelectedEvent(savedEvent);
        setShowEventModal(false);
        setNewEventName('');
        setNewEventDate('');
        setNewEventDescription('');
        setNewEventLogoUrl('');
        setNewEventIntermissionMessage('');
      }
    } catch (err) {
      // Fallback: save locally
      setEvents([...events, event]);
      setSelectedEvent(event);
      setShowEventModal(false);
      setNewEventName('');
      setNewEventDate('');
      setNewEventDescription('');
      setNewEventLogoUrl('');
      setNewEventIntermissionMessage('');
    }
  };
  const openEditEventLogo = (event: HitsterEvent) => {
    setEditingEvent(event);
    setEditEventName(event.name);
    setEditEventLogoUrl(event.logoUrl || '');
    setEditEventIntermissionMessage(event.intermissionMessage || '');
  };
  const saveEventLogo = async () => {
    if (!editingEvent || !editEventName.trim()) return;
    const isCurrentlySelectedEvent = selectedEvent?.id === editingEvent.id;
    const updatedEvent = {
      ...editingEvent,
      name: editEventName.trim(),
      logoUrl: editEventLogoUrl || undefined,
      intermissionMessage: editEventIntermissionMessage.trim() || undefined,
      updatedAt: new Date().toISOString(),
    };
    try {
      await fetch(`${API_BASE}/api/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Failed to save event logo:', err);
    }
    // Update local state
    setEvents(events.map(e => e.id === editingEvent.id ? updatedEvent : e));
    if (isCurrentlySelectedEvent) {
      setSelectedEvent(updatedEvent);

      try {
        await fetch(`${API_BASE}/api/display-state`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            eventName: updatedEvent.name,
            eventLogoUrl: updatedEvent.logoUrl,
            intermissionMessage: updatedEvent.intermissionMessage
          })
        });
      } catch (err) {
        console.error('Failed to sync updated event settings to display:', err);
      }
    }
    setEditingEvent(null);
    setEditEventName('');
    setEditEventLogoUrl('');
    setEditEventIntermissionMessage('');
  };
  const createRound = async () => {
    if (!selectedEvent || !newRoundName.trim()) return;
    const round: EventRound = {
      id: `round-${Date.now()}`,
      name: newRoundName,
      order: selectedEvent.rounds.length + 1,
      songs: [],
      hitsterVariant: newRoundVariant,
    };
    const updatedEvent = {
      ...selectedEvent,
      rounds: [...selectedEvent.rounds, round],
      updatedAt: new Date().toISOString(),
    };
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error saving round:', err);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setSelectedRound(round);
    setShowRoundModal(false);
    setNewRoundName('');
    setNewRoundVariant('classic');
  };

  const updateRoundVariant = async (variant: HitsterRoundVariant) => {
    if (!selectedEvent || !selectedRound) return;

    const updatedRound: EventRound = {
      ...selectedRound,
      hitsterVariant: variant,
    };
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map((round) => round.id === updatedRound.id ? updatedRound : round),
      updatedAt: new Date().toISOString(),
    };

    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error saving round variant:', err);
    }

    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };
  const deleteRound = async (roundId: string) => {
    if (!selectedEvent) return;
    
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.filter(r => r.id !== roundId),
      updatedAt: new Date().toISOString(),
    };
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error deleting round:', err);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    if (selectedRound?.id === roundId) {
      setSelectedRound(null);
    }
  };
  // Build search query
  const buildSearchQuery = useCallback((): string => {
    const parts: string[] = [];
    
    // Track if we have any actual search terms (not just filters)
    let hasSearchTerm = false;
    let hasYearFilter = false;
    
    if (artistFilter.trim()) {
      parts.push(`artist:${artistFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (trackFilter.trim()) {
      parts.push(`track:${trackFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (albumFilter.trim()) {
      parts.push(`album:${albumFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (genreFilter.trim()) {
      parts.push(`genre:${genreFilter.trim()}`);
      hasSearchTerm = true;
    }
    if (yearFrom && yearTo) {
      parts.push(`year:${yearFrom}-${yearTo}`);
      hasYearFilter = true;
    } else if (yearFrom) {
      parts.push(`year:${yearFrom}-${new Date().getFullYear()}`);
      hasYearFilter = true;
    } else if (yearTo) {
      parts.push(`year:1900-${yearTo}`);
      hasYearFilter = true;
    }
    if (searchQuery.trim()) {
      parts.push(searchQuery.trim());
      hasSearchTerm = true;
    }
    
    // If only year filter is set, add a wildcard genre to allow year-only search
    // Spotify needs some search term, so we add common genres as wildcards
    if (!hasSearchTerm && hasYearFilter) {
      // Use genre:pop as a broad base, or just search for common music terms
      parts.unshift('*');
      hasSearchTerm = true;
    }
    
    // Return empty if no search terms at all
    if (!hasSearchTerm && !hasYearFilter) {
      return '';
    }
    
    return parts.join(' ');
  }, [artistFilter, trackFilter, albumFilter, genreFilter, yearFrom, yearTo, searchQuery]);
  const handleDecadeClick = (decade: typeof DECADES[0]) => {
    if (selectedDecade === decade.label) {
      setSelectedDecade(null);
      setYearFrom('');
      setYearTo('');
    } else {
      setSelectedDecade(decade.label);
      setYearFrom(decade.start.toString());
      setYearTo(decade.end.toString());
    }
  };
  const clearFilters = () => {
    setSearchQuery('');
    setArtistFilter('');
    setTrackFilter('');
    setAlbumFilter('');
    setYearFrom('');
    setYearTo('');
    setSelectedDecade(null);
    setGenreFilter('');
    setOnlyWithPreview(false);
    setSearchResults([]);
    setShowSpotifyResultsModal(false);
  };
  const handleSearch = async () => {
    if (/^https?:\/\/(www\.)?(youtube\.com|youtu\.be|dailymotion\.com)\//i.test(searchQuery.trim())) {
      setError('Deze URL hoort bij de YouTube-modus. Klik bovenaan op "📺 YouTube" en plak de URL daar.');
      return;
    }

    const query = buildSearchQuery();
    if (!query) {
      setError('Vul minimaal een filter of zoekterm in.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${API_BASE}/api/spotify/search?query=${encodeURIComponent(query)}&limit=${searchLimit}`
      );
      const data = await response.json();
      if (data.error) {
        setError(data.message || data.error);
      } else {
        let results = data.results || [];
        
        // Filter by year range (client-side, since Spotify doesn't always respect year filter)
        if (yearFrom || yearTo) {
          const fromYear = yearFrom ? parseInt(yearFrom) : 1900;
          const toYear = yearTo ? parseInt(yearTo) : new Date().getFullYear();
          results = results.filter((t: SpotifyTrack) => {
            if (!t.year) return false;
            return t.year >= fromYear && t.year <= toYear;
          });
        }
        
        if (onlyWithPreview) {
          results = results.filter((t: SpotifyTrack) => t.previewUrl);
        }
        setSearchResults(results);
        setShowSpotifyResultsModal(results.length > 0);
        if (results.length === 0) {
          setError('Geen resultaten gevonden');
        }
      }
    } catch (err) {
      setError('Zoeken mislukt. Is de server actief?');
    } finally {
      setLoading(false);
    }
  };
  const getSpotifyRecommendedGain = async (spotifyId: string): Promise<number | undefined> => {
    if (!spotifyId) return undefined;
    const cached = spotifyGainCacheRef.current[spotifyId];
    if (typeof cached === 'number') return cached;

    try {
      const response = await fetch(`${API_BASE}/api/spotify/loudness/${spotifyId}`);
      if (!response.ok) return undefined;
      const data = await response.json();
      if (typeof data.recommendedGain === 'number') {
        spotifyGainCacheRef.current[spotifyId] = data.recommendedGain;
        return data.recommendedGain;
      }
    } catch (err) {
      console.warn('Could not get Spotify loudness for track:', spotifyId);
    }
    return undefined;
  };

  const addSongToRound = async (track: SpotifyTrack) => {
    if (!selectedEvent) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const isThemeNight = getEventType(selectedEvent) === 'theme-night';
    const targetSongs = selectedRound?.songs || (isThemeNight ? (selectedEvent.themeSongs || []) : null);

    if (!targetSongs) {
      setError('Selecteer eerst een ronde of thema-avond');
      return;
    }

    // Check if already added
    if (targetSongs.some(s => s.spotifyId === track.id)) {
      return;
    }
    const recommendedGain = typeof track.recommendedGain === 'number'
      ? track.recommendedGain
      : await getSpotifyRecommendedGain(track.id);

    const song: EventSong = {
      id: `song-${Date.now()}`,
      spotifyId: track.id,
      title: track.title,
      artist: track.artist,
      album: track.album,
      year: track.year || new Date().getFullYear(),
      duration: track.duration,
      imageUrl: track.thumbnail || undefined,
      previewUrl: track.previewUrl || undefined,
      spotifyUri: `spotify:track:${track.id}`,
      volumeGain: recommendedGain !== undefined && recommendedGain !== 1 ? recommendedGain : undefined,
      order: targetSongs.length + 1,
    };

    let updatedRound: EventRound | null = null;
    let updatedEvent: HitsterEvent;

    if (selectedRound) {
      updatedRound = {
        ...selectedRound,
        songs: [...selectedRound.songs, song],
      };
      updatedEvent = {
        ...selectedEvent,
        rounds: selectedEvent.rounds.map(r => r.id === updatedRound!.id ? updatedRound! : r),
        updatedAt: new Date().toISOString(),
      };
    } else {
      updatedEvent = {
        ...selectedEvent,
        themeSongs: [...(selectedEvent.themeSongs || []), song],
        updatedAt: new Date().toISOString(),
      };
    }

    // Save to server
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));

    if (updatedRound) {
      setSelectedRound(updatedRound);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };
  const addMultipleSongsToRound = (songs: EventSong[]) => {
    if (!selectedEvent) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const isThemeNight = getEventType(selectedEvent) === 'theme-night';
    const existingSongs = selectedRound?.songs || (isThemeNight ? (selectedEvent.themeSongs || []) : null);

    if (!existingSongs) {
      setError('Selecteer eerst een ronde of thema-avond');
      return;
    }

    // Filter out duplicates
    const existingIds = new Set(existingSongs.map(s => s.spotifyId || s.title + s.artist));
    const newSongs = songs.filter(s => !existingIds.has(s.spotifyId || s.title + s.artist));
    if (newSongs.length === 0) return;

    let updatedRound: EventRound | null = null;
    let updatedEvent: HitsterEvent;

    if (selectedRound) {
      updatedRound = {
        ...selectedRound,
        songs: [...selectedRound.songs, ...newSongs.map((s, i) => ({
          ...s,
          order: selectedRound.songs.length + i + 1,
        }))],
      };
      updatedEvent = {
        ...selectedEvent,
        rounds: selectedEvent.rounds.map(r => r.id === updatedRound!.id ? updatedRound! : r),
        updatedAt: new Date().toISOString(),
      };
    } else {
      updatedEvent = {
        ...selectedEvent,
        themeSongs: [...existingSongs, ...newSongs.map((s, i) => ({
          ...s,
          order: existingSongs.length + i + 1,
        }))],
        updatedAt: new Date().toISOString(),
      };
    }

    // Save to server
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));

    if (updatedRound) {
      setSelectedRound(updatedRound);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  const addMultipleSongsToThemeNight = (songs: EventSong[]) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') {
      setError('Selecteer eerst een thema-avond');
      return;
    }

    const existingSongs = selectedEvent.themeSongs || [];
    const existingIds = new Set(existingSongs.map(s => s.spotifyId || s.title + s.artist));
    const newSongs = songs.filter(s => !existingIds.has(s.spotifyId || s.title + s.artist));
    if (newSongs.length === 0) return;

    const updatedThemeSongs = [
      ...existingSongs,
      ...newSongs.map((song, index) => ({
        ...song,
        order: existingSongs.length + index + 1,
      })),
    ];

    const updatedEvent: HitsterEvent = {
      ...selectedEvent,
      themeSongs: updatedThemeSongs,
      updatedAt: new Date().toISOString(),
    };

    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));

    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };
  const removeSongFromRound = (songId: string) => {
    if (!selectedEvent) return;

    if (!selectedRound && getEventType(selectedEvent) === 'theme-night') {
      const updatedThemeSongs = (selectedEvent.themeSongs || []).filter(s => s.id !== songId);
      const updatedEvent = {
        ...selectedEvent,
        themeSongs: updatedThemeSongs,
        updatedAt: new Date().toISOString(),
      };
      fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      }).catch(err => console.error('Error saving:', err));
      setSelectedEvent(updatedEvent);
      setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      return;
    }

    if (!selectedRound) return;
    const updatedRound = {
      ...selectedRound,
      songs: selectedRound.songs.filter(s => s.id !== songId),
    };
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(r => r.id === updatedRound.id ? updatedRound : r),
      updatedAt: new Date().toISOString(),
    };
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));
    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };

  const moveSongToRound = (songId: string, targetRoundId: string) => {
    if (!selectedEvent || !selectedRound || targetRoundId === selectedRound.id) return;
    const song = selectedRound.songs.find(s => s.id === songId);
    if (!song) return;
    const targetRound = selectedEvent.rounds.find(r => r.id === targetRoundId);
    if (!targetRound) return;
    const updatedSourceRound = {
      ...selectedRound,
      songs: selectedRound.songs.filter(s => s.id !== songId),
    };
    const updatedTargetRound = {
      ...targetRound,
      songs: [...targetRound.songs, { ...song, order: targetRound.songs.length + 1 }],
    };
    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(r => {
        if (r.id === updatedSourceRound.id) return updatedSourceRound;
        if (r.id === updatedTargetRound.id) return updatedTargetRound;
        return r;
      }),
      updatedAt: new Date().toISOString(),
    };
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));
    setSelectedRound(updatedSourceRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setMovingSongId(null);
  };
  const reorderSongsById = (songs: EventSong[], sourceId: string, targetId: string) => {
    const sourceIndex = songs.findIndex(song => song.id === sourceId);
    const targetIndex = songs.findIndex(song => song.id === targetId);

    if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
      return songs;
    }

    const reorderedSongs = [...songs];
    const [movedSong] = reorderedSongs.splice(sourceIndex, 1);
    reorderedSongs.splice(targetIndex, 0, movedSong);

    return reorderedSongs.map((song, index) => ({
      ...song,
      order: index + 1,
    }));
  };
  const moveSongInSelectedRoundByDrop = async (sourceId: string, targetId: string) => {
    if (!selectedEvent || !selectedRound) return;

    const normalizedSongs = reorderSongsById(selectedRound.songs, sourceId, targetId);
    if (normalizedSongs === selectedRound.songs) return;

    const updatedRound = {
      ...selectedRound,
      songs: normalizedSongs,
    };

    const updatedEvent = {
      ...selectedEvent,
      rounds: selectedEvent.rounds.map(r => r.id === updatedRound.id ? updatedRound : r),
      updatedAt: new Date().toISOString(),
    };

    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
    } catch (err) {
      console.error('Error moving song by drag and drop:', err);
      return;
    }

    setSelectedRound(updatedRound);
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
  };
  const handleSongDragStart = (songId: string, event: React.DragEvent<HTMLElement>) => {
    event.dataTransfer.effectAllowed = 'move';
    event.dataTransfer.setData('text/plain', songId);
    setDraggedSongId(songId);
  };
  const handleSongDragOver = (songId: string, event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();
    if (songId !== draggedSongId) {
      setDropTargetSongId(songId);
    }
  };
  const handleSongDrop = async (targetSongId: string, event: React.DragEvent<HTMLElement>) => {
    event.preventDefault();

    if (draggedSongId && draggedSongId !== targetSongId) {
      await moveSongInSelectedRoundByDrop(draggedSongId, targetSongId);
    }

    setDraggedSongId(null);
    setDropTargetSongId(null);
  };
  const handleSongDragEnd = () => {
    setDraggedSongId(null);
    setDropTargetSongId(null);
  };
  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-NL', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };
  // Audio playback for round songs
  const playSongAudio = (song: EventSong) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingYouTubeId(null);
    // If clicking the same song, just stop
    if (playingSongId === song.id) {
      setPlayingSongId(null);
      return;
    }
    // Find audio source
    const audioUrl = song.audioUrl || song.previewUrl;
    if (audioUrl) {
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      audio.play();
      setPlayingSongId(song.id);
      audio.onended = () => {
        setPlayingSongId(null);
        audioRef.current = null;
      };
    }
  };
  const playYouTubeSong = (song: EventSong) => {
    // Stop any currently playing audio
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    setPlayingSongId(null);
    // Toggle YouTube player
    if (playingYouTubeId === song.id) {
      setPlayingYouTubeId(null);
    } else {
      setPlayingYouTubeId(song.id);
    }
  };
  // Live Display functions
  const playOnDisplay = async (song: EventSong, round: EventRound, roundIndex: number) => {
    const now = Date.now();
    const isRapidRepeat =
      lastDisplayActionRef.current.songId === song.id &&
      now - lastDisplayActionRef.current.startedAt < 1500;

    let isAlreadyLive = liveSongIdRef.current === song.id;

    if (isAlreadyLive) {
      try {
        const liveStateResponse = await fetch(`${API_BASE}/api/display-state`);
        if (liveStateResponse.ok) {
          const liveState = await liveStateResponse.json();
          applyDisplayStateSnapshot(liveState);
          isAlreadyLive = !!(liveState.mode === 'hitster' && liveState.isPlaying && liveState.currentSong?.id === song.id);
        } else {
          isAlreadyLive = false;
        }
      } catch (err) {
        console.warn('Could not verify live display state before playing song:', err);
        isAlreadyLive = false;
      }
    }

    if (displayActionInFlightRef.current || isRapidRepeat || isAlreadyLive) {
      console.log('⏭️ Ignoring duplicate display action:', {
        songId: song.id,
        inFlight: displayActionInFlightRef.current,
        isRapidRepeat,
        isAlreadyLive
      });
      return;
    }

    displayActionInFlightRef.current = true;
    lastDisplayActionRef.current = { songId: song.id, startedAt: now };
    liveSongIdRef.current = song.id;
    liveSongOrderRef.current = song.order ?? null;
    liveRoundSongCountRef.current = round.songs.length;
    liveRoundOrderRef.current = round.order || roundIndex + 1;
    queuedDisplaySelectionRef.current = null;
    setArmedDisplaySongId(null);
    setStartingDisplaySongId(song.id);

    console.log('🎵 playOnDisplay called with song:', {
      id: song.id,
      title: song.title,
      youtubeVideoId: song.youtubeVideoId,
      youtubeStartTime: song.youtubeStartTime,
      youtubeEndTime: song.youtubeEndTime
    });
    void pushLog('info', '▶ Nummer gestart op display', {
      title: song.title,
      artist: (song as any).artist,
      year: (song as any).year,
      type: song.spotifyUri && !song.audioUrl && !song.previewUrl ? 'Spotify'
          : song.youtubeVideoId ? 'YouTube'
          : song.audioUrl || song.previewUrl ? 'Audio'
          : 'Onbekend'
    });
    try {
      const roundVariant = round.hitsterVariant || 'classic';
      const selectedGenerationPeriod = roundVariant === 'battle-generations'
        ? HITSTER_GENERATION_PERIODS.find((period) => period.id === selectedGenerationPeriodId)
        : null;
      const songGenerationPeriod = getGenerationPeriodForYear(song.year);

      if (roundVariant === 'battle-generations' && selectedGenerationPeriod && songGenerationPeriod?.id !== selectedGenerationPeriod.id) {
        setError(`Dit nummer hoort bij ${songGenerationPeriod?.label || 'een andere periode'}, niet bij ${selectedGenerationPeriod.label}.`);
        return;
      }

      const requiresDisplaySpotify = !!(song.spotifyUri && !song.audioUrl && !song.previewUrl);
      let displaySpotifyReadyForSong = true;
      let displaySpotifyMessage: string | null = null;
      let displayConfirmationMessage: string | null = null;
      let displaySpotifyNeedsWarmup = false;

      if (requiresDisplaySpotify) {
        try {
          const { displayClient, checkedAt } = await getDisplayClientStatus();
          const isRecentlySeen = typeof displayClient.lastSeen === 'number' && checkedAt - displayClient.lastSeen < 15000;

          displaySpotifyReadyForSong = !!(
            isRecentlySeen &&
            displayClient.spotifyAuthenticated &&
            displayClient.spotifyPremium
          );
          displaySpotifyNeedsWarmup = displaySpotifyReadyForSong && !displayClient.spotifyReady;

          if (!isRecentlySeen) {
            displaySpotifyReadyForSong = false;
            displaySpotifyMessage = 'Het display-scherm is niet actief of meldt zich niet terug. Open het display-scherm voordat je een Spotify-nummer start.';
          } else if (!displayClient.spotifyAuthenticated) {
            displaySpotifyReadyForSong = false;
            displaySpotifyMessage = 'Spotify is niet ingelogd op het display-scherm. Log daar in met Spotify Premium om dit nummer af te spelen.';
          } else if (!displayClient.spotifyPremium) {
            displaySpotifyReadyForSong = false;
            displaySpotifyMessage = 'Het display-scherm heeft geen Spotify Premium. Spotify-nummers kunnen daar niet worden afgespeeld.';
          } else {
            void pushLog('info', displaySpotifyNeedsWarmup ? '⏳ Display Spotify nog aan het opstarten...' : '✅ Display Spotify gereed', {
              spotifyReady: displayClient.spotifyReady,
              spotifyAuthenticated: displayClient.spotifyAuthenticated,
              spotifyPremium: displayClient.spotifyPremium
            });
          }
        } catch (displayStatusErr) {
          console.warn('Could not verify display Spotify status:', displayStatusErr);
          displaySpotifyReadyForSong = false;
          displaySpotifyMessage = displayStatusErr instanceof Error
            ? displayStatusErr.message
            : 'De status van Spotify op het display-scherm kon niet worden gecontroleerd.';
        }
      }

      // Update display state
      await fetch(`${API_BASE}/api/display-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'hitster',
          isPlaying: true,
          isIntermission: false,
          waitingMessage: null,
          currentSongOrder: song.order,
          youtubeVolume: 100,
          youtubePaused: false,
          showResults: false,
          quizQuestion: null,
          currentSong: {
            id: song.id,
            title: song.title,
            artist: song.artist,
            year: song.year,
            order: song.order,
            imageUrl: song.imageUrl,
            youtubeVideoId: song.youtubeVideoId,
            youtubeStartTime: song.youtubeStartTime,
            youtubeEndTime: song.youtubeEndTime,
            audioStartTime: song.audioStartTime,
            volumeGain: song.volumeGain,
            audioUrl: song.audioUrl,
            previewUrl: song.previewUrl,
            lyrics: song.lyrics,
            showLyrics: song.showLyrics,
            spotifyUri: song.spotifyUri,
            yearSource: song.yearSource
          },
          eventName: selectedEvent?.name || '',
          eventLogoUrl: selectedEvent?.logoUrl,
          roundName: round.name || '',
          roundNumber: roundIndex + 1,
          hitsterRoundVariant: roundVariant,
          hitsterGenerationPeriodId: selectedGenerationPeriod?.id || null,
          hitsterGenerationPeriodLabel: selectedGenerationPeriod
            ? `${selectedGenerationPeriod.label} (${selectedGenerationPeriod.yearLabel})`
            : null,
          hitsterGenerationPeriodIconUrl: selectedGenerationPeriod?.iconUrl || null
        })
      });
      setLiveSongId(song.id);
      setIsIntermissionActive(false);
      setIsYoutubePaused(false); // Reset pause state for new song

      // Stop any dashboard-side preview/playback so only the display plays.
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (overviewAudioRef.current) {
        overviewAudioRef.current.pause();
        overviewAudioRef.current = null;
      }

      setPlayingYouTubeId(null);
      setPlayingSongId(null);

      if (overviewPlayingSongId === song.id) {
        setOverviewPlayingSongId(null);
      }

      try {
        await pause();
      } catch (pauseErr) {
        console.warn('Display start: local Spotify pause skipped:', pauseErr);
      }

      if (requiresDisplaySpotify && displaySpotifyReadyForSong) {
        for (let attempt = 0; attempt < 12; attempt += 1) {
          await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 750);
          });

          try {
            const { displayClient, checkedAt } = await getDisplayClientStatus();
            const isRecentlySeen = typeof displayClient.lastSeen === 'number' && checkedAt - displayClient.lastSeen < 15000;
            const matchesSong = displayClient.currentSongId === song.id;
            const matchesUri = displayClient.actualSpotifyUri === song.spotifyUri;
            const confirmed = !!(displayClient.playbackConfirmed && matchesSong && matchesUri);

            if (confirmed) {
              displayConfirmationMessage = null;
              displaySpotifyNeedsWarmup = false;
              void pushLog('ok', `✅ Spotify bevestigd op display (poging ${attempt + 1})`, {
                currentSongId: displayClient.currentSongId,
                actualSpotifyUri: displayClient.actualSpotifyUri
              });
              break;
            }

            if (!isRecentlySeen) {
              displayConfirmationMessage = 'Het display-scherm is tijdens het starten offline gegaan of meldt zich niet meer terug.';
              break;
            }

            if (!displayClient.spotifyAuthenticated) {
              displayConfirmationMessage = 'Spotify is niet meer ingelogd op het display-scherm.';
              break;
            }

            if (!displayClient.spotifyPremium) {
              displayConfirmationMessage = 'Het display-scherm heeft geen Spotify Premium meer.';
              break;
            }

            if (displayClient.error) {
              displayConfirmationMessage = `Spotify op het display meldde een fout: ${displayClient.error}`;
              break;
            }

            displaySpotifyNeedsWarmup = !displayClient.spotifyReady;

            if (attempt === 11) {
              displayConfirmationMessage = displayClient.spotifyReady
                ? 'Het display-scherm bevestigt niet dat dit Spotify-nummer echt gestart is.'
                : 'Spotify op het display-scherm is nog aan het opstarten. Probeer het nog een keer of herlaad het display-scherm.';
            }
          } catch (confirmationErr) {
            if (attempt === 11) {
              displayConfirmationMessage = 'De bevestiging van het display-scherm kon niet worden opgehaald.';
            }
          }
        }
      }

      if (!displaySpotifyReadyForSong && displaySpotifyMessage) {
        setError(displaySpotifyMessage);
        void pushLog('error', '❌ Display Spotify niet beschikbaar', { reden: displaySpotifyMessage });
        console.warn('📺 Display cannot play this Spotify-only song yet:', {
          songId: song.id,
          spotifyUri: song.spotifyUri,
          reason: displaySpotifyMessage
        });
      } else if (displayConfirmationMessage) {
        setError(displayConfirmationMessage);
        void pushLog('warn', '⚠️ Spotify niet bevestigd op display', {
          reden: displayConfirmationMessage,
          warmupPending: displaySpotifyNeedsWarmup
        });
        console.warn('📺 Display did not confirm Spotify playback:', {
          songId: song.id,
          spotifyUri: song.spotifyUri,
          reason: displayConfirmationMessage,
          warmupPending: displaySpotifyNeedsWarmup
        });
      } else {
        setError(null);
        registerThemeNightPlayedSong(song);
        void pushLog('ok', '🎵 Afspelen succesvol op display', {
          spotifyUri: song.spotifyUri ?? null,
          youtubeVideoId: song.youtubeVideoId ?? null,
          hasAudioUrl: !!(song.audioUrl || song.previewUrl)
        });
        console.log('📺 Display handles playback for this song:', {
          songId: song.id,
          spotifyUri: song.spotifyUri,
          youtubeVideoId: song.youtubeVideoId,
          hasAudioUrl: !!(song.audioUrl || song.previewUrl)
        });
      }
    } catch (err) {
      void pushLog('error', '❌ Fout bij starten display', { error: String(err) });
      console.error('Failed to update display:', err);
      liveSongIdRef.current = liveSongId;
    } finally {
      displayActionInFlightRef.current = false;
      setStartingDisplaySongId(current => current === song.id ? null : current);
    }
  };

  const clearPlayAllTimer = () => {
    if (playAllTimeoutRef.current) {
      clearTimeout(playAllTimeoutRef.current);
      playAllTimeoutRef.current = null;
    }
  };

  const armSongForDisplay = async (song: EventSong, round: EventRound, roundIndex: number) => {
    const roundNumber = round.order || roundIndex + 1;
    const roundVariant = round.hitsterVariant || 'classic';
    const selectedGenerationPeriod = roundVariant === 'battle-generations'
      ? HITSTER_GENERATION_PERIODS.find((period) => period.id === selectedGenerationPeriodId)
      : null;

    if (roundVariant === 'battle-generations') {
      const songGenerationPeriod = getGenerationPeriodForYear(song.year);
      if (selectedGenerationPeriod && songGenerationPeriod?.id !== selectedGenerationPeriod.id) {
        setError(`Dit nummer hoort bij ${songGenerationPeriod?.label || 'een andere periode'}, niet bij ${selectedGenerationPeriod.label}.`);
        return;
      }
    }

    queuedDisplaySelectionRef.current = {
      songId: song.id,
      songOrder: song.order,
      roundNumber,
      roundName: round.name || '',
      hitsterGenerationPeriodId: selectedGenerationPeriod?.id || null,
      hitsterGenerationPeriodLabel: selectedGenerationPeriod
        ? `${selectedGenerationPeriod.label} (${selectedGenerationPeriod.yearLabel})`
        : null,
      hitsterGenerationPeriodIconUrl: selectedGenerationPeriod?.iconUrl || null
    };
    setArmedDisplaySongId(song.id);

    if (liveSongIdRef.current) {
      return;
    }

    try {
      await fetch(`${API_BASE}/api/display-state`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mode: 'hitster',
          isPlaying: false,
          isIntermission: false,
          waitingMessage: null,
          currentSongOrder: song.order,
          youtubeVolume: 100,
          youtubePaused: false,
          showResults: false,
          currentSong: null,
          eventName: selectedEvent?.name || '',
          eventLogoUrl: selectedEvent?.logoUrl,
          roundName: round.name || '',
          roundNumber,
          hitsterRoundVariant: roundVariant,
          hitsterGenerationPeriodId: selectedGenerationPeriod?.id || null,
          hitsterGenerationPeriodLabel: selectedGenerationPeriod
            ? `${selectedGenerationPeriod.label} (${selectedGenerationPeriod.yearLabel})`
            : null,
          hitsterGenerationPeriodIconUrl: selectedGenerationPeriod?.iconUrl || null
        })
      });
      setIsIntermissionActive(false);
    } catch (err) {
      console.error('Failed to arm song for display:', err);
    }
  };

  const playAllRounds = async () => {
    if (!selectedEvent) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const queue = selectedEvent.rounds.flatMap((round, roundIndex) =>
      round.songs.map(song => ({
        song,
        round,
        roundNumber: round.order || roundIndex + 1,
      }))
    );

    if (queue.length === 0) {
      setError('Dit evenement heeft nog geen nummers');
      return;
    }

    playAllActiveRef.current = true;
    setIsPlayingAllRounds(true);
    setError(null);

    const playAtIndex = async (index: number) => {
      if (!playAllActiveRef.current) return;

      if (index >= queue.length) {
        setPlayAllStatus('Alle rondes zijn afgespeeld');
        setIsPlayingAllRounds(false);
        playAllActiveRef.current = false;
        clearPlayAllTimer();
        return;
      }

      const current = queue[index];
      setSelectedRound(current.round);
      setPlayAllStatus(`Speelt ${index + 1}/${queue.length}: ${current.song.artist} - ${current.song.title}`);

      await playOnDisplay(current.song, current.round, current.roundNumber - 1);

      const clipDuration = current.song.youtubeStartTime !== undefined && current.song.youtubeEndTime !== undefined
        ? Math.max(1, current.song.youtubeEndTime - current.song.youtubeStartTime)
        : 0;
      const durationSeconds = clipDuration > 0
        ? clipDuration
        : (current.song.duration && current.song.duration > 0 ? current.song.duration : 30);

      clearPlayAllTimer();
      playAllTimeoutRef.current = setTimeout(() => {
        playAtIndex(index + 1);
      }, (durationSeconds + 1) * 1000);
    };

    clearPlayAllTimer();
    playAtIndex(0);
  };

  const stopPlayAllRounds = () => {
    playAllActiveRef.current = false;
    clearPlayAllTimer();
    setIsPlayingAllRounds(false);
    setPlayAllStatus(null);
  };

  useEffect(() => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') {
      setThemeNightSetSongIds([]);
      setThemeNightSetStatus(null);
      setThemeNightSetHistory([]);
      setSelectedThemeNightSetId(null);
      setThemeNightSongSetMap({});
      setThemeNightOverviewSetFilter('all');
      return;
    }

    const storageKey = `theme-night-current-set:${selectedEvent.id}`;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) {
        setThemeNightSetSongIds([]);
        return;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        setThemeNightSetSongIds(parsed.filter((item) => typeof item === 'string'));
      } else {
        setThemeNightSetSongIds([]);
      }
    } catch {
      setThemeNightSetSongIds([]);
    }
  }, [selectedEvent]);

  const loadThemeNightSetHistory = useCallback(async () => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') {
      setThemeNightSetHistory([]);
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${THEME_NIGHT_ROUND_ID}`);
      if (!response.ok) {
        setThemeNightSetHistory([]);
        setThemeNightSongSetMap({});
        return;
      }

      const entries = await response.json();
      if (!Array.isArray(entries)) {
        setThemeNightSetHistory([]);
        setThemeNightSongSetMap({});
        return;
      }

      const grouped = new Map<string, { count: number; lastPlayedAt: string | null }>();
      const songSets: Record<string, Set<number>> = {};
      entries.forEach((entry: any) => {
        const setId = typeof entry?.setRoundId === 'string' ? entry.setRoundId : null;
        if (!setId) return;
        const setNumber = getThemeNightSetNumberFromId(setId);
        if (setNumber && typeof entry?.songId === 'string') {
          if (!songSets[entry.songId]) {
            songSets[entry.songId] = new Set<number>();
          }
          songSets[entry.songId].add(setNumber);
        }
        const current = grouped.get(setId) || { count: 0, lastPlayedAt: null };
        const playedAt = typeof entry?.playedAt === 'string' ? entry.playedAt : null;
        const nextLastPlayedAt = !current.lastPlayedAt
          ? playedAt
          : (playedAt && playedAt > current.lastPlayedAt ? playedAt : current.lastPlayedAt);

        grouped.set(setId, {
          count: current.count + 1,
          lastPlayedAt: nextLastPlayedAt,
        });
      });

      const history = Array.from(grouped.entries())
        .map(([setId, data]) => ({ setId, ...data }))
        .sort((a, b) => {
          const aTime = a.lastPlayedAt || '';
          const bTime = b.lastPlayedAt || '';
          return bTime.localeCompare(aTime);
        });

      setThemeNightSetHistory(history);
      const songSetMap: Record<string, number[]> = {};
      Object.keys(songSets).forEach((songId) => {
        songSetMap[songId] = Array.from(songSets[songId]).sort((a, b) => a - b);
      });
      setThemeNightSongSetMap(songSetMap);
    } catch {
      setThemeNightSetHistory([]);
      setThemeNightSongSetMap({});
    }
  }, [selectedEvent]);

  useEffect(() => {
    void loadThemeNightSetHistory();
  }, [loadThemeNightSetHistory]);

  const saveThemeNightSetToAnswerKeys = useCallback(async (songIds: string[]) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night' || songIds.length !== 10) {
      return;
    }

    setThemeNightSetSaving(true);
    const highestKnownSetNumber = themeNightSetHistory.reduce((max, item) => {
      const parsed = getThemeNightSetNumberFromId(item.setId);
      return parsed ? Math.max(max, parsed) : max;
    }, 0);
    const setRoundId = `theme-night-set-${highestKnownSetNumber + 1}`;
    const baseTimestamp = Date.now();
    const entries = songIds.map((songId, index) => ({
      songId,
      categoryId: 'theme-night-set',
      playedAt: new Date(baseTimestamp + index).toISOString(),
      order: index + 1,
      setRoundId,
    }));

    try {
      await Promise.all([
        fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${setRoundId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries }),
        }),
        fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${THEME_NIGHT_ROUND_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries }),
        })
      ]);

      setThemeNightSetSongIds([]);
      localStorage.removeItem(`theme-night-current-set:${selectedEvent.id}`);
      setThemeNightSetStatus(`Set opgeslagen: ${setRoundId}`);
      void loadThemeNightSetHistory();
    } catch (err) {
      console.error('Failed to save theme-night answer set:', err);
      setError('Kon de thema-avond set niet opslaan in antwoordsleutels.');
    } finally {
      setThemeNightSetSaving(false);
    }
  }, [loadThemeNightSetHistory, selectedEvent, themeNightSetHistory]);

  const addSongToManualThemeNightSet = useCallback(async (song: EventSong) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night') {
      return;
    }

    const highestKnownSetNumber = themeNightSetHistory.reduce((max, item) => {
      const parsed = getThemeNightSetNumberFromId(item.setId);
      return parsed ? Math.max(max, parsed) : max;
    }, 0);
    const suggestedSetNumber = highestKnownSetNumber + 1;
    const raw = window.prompt('Naar welke set wil je dit nummer toevoegen? (1,2,3,...)', String(suggestedSetNumber));
    if (!raw) return;

    const requestedSetNumber = Number(raw.trim());
    if (!Number.isFinite(requestedSetNumber) || requestedSetNumber < 1 || requestedSetNumber > 999) {
      setError('Vul een geldig setnummer in (1 t/m 999).');
      return;
    }

    const setRoundId = `theme-night-set-${requestedSetNumber}`;

    try {
      const existingResponse = await fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${setRoundId}`);
      const existingEntries = existingResponse.ok ? await existingResponse.json() : [];
      const existingCount = Array.isArray(existingEntries) ? existingEntries.length : 0;
      const alreadyInSet = Array.isArray(existingEntries)
        ? existingEntries.some((entry: any) => entry?.songId === song.id)
        : false;

      if (alreadyInSet) {
        setThemeNightSetStatus(`${song.artist} - ${song.title} staat al in set ${requestedSetNumber}.`);
        return;
      }

      const entry = {
        songId: song.id,
        categoryId: 'theme-night-set-manual',
        playedAt: new Date().toISOString(),
        order: existingCount + 1,
        setRoundId,
      };

      await Promise.all([
        fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${setRoundId}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: [entry] }),
        }),
        fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${THEME_NIGHT_ROUND_ID}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: [entry] }),
        })
      ]);

      setThemeNightSetStatus(`Toegevoegd aan set ${requestedSetNumber} (${existingCount + 1}/10)`);
      void loadThemeNightSetHistory();
    } catch (err) {
      console.error('Failed to add song to manual theme set:', err);
      setError('Kon nummer niet handmatig aan set toevoegen.');
    }
  }, [loadThemeNightSetHistory, selectedEvent, themeNightSetHistory]);

  const registerThemeNightPlayedSong = useCallback((song: EventSong) => {
    if (!selectedEvent || getEventType(selectedEvent) !== 'theme-night' || selectedRound || themeNightSetSaving) {
      return;
    }

    setThemeNightSetSongIds((prev) => {
      if (prev.includes(song.id)) {
        return prev;
      }

      const next = [...prev, song.id];
      localStorage.setItem(`theme-night-current-set:${selectedEvent.id}`, JSON.stringify(next));
      setThemeNightSetStatus(`Thema-set: ${next.length}/10`);

      if (next.length === 10) {
        void saveThemeNightSetToAnswerKeys(next);
      }

      return next;
    });
  }, [saveThemeNightSetToAnswerKeys, selectedEvent, selectedRound, themeNightSetSaving]);

  useEffect(() => {
    return () => {
      playAllActiveRef.current = false;
      clearPlayAllTimer();
    };
  }, []);

  const revealOnDisplay = async () => {
    try {
      await fetch(`${API_BASE}/api/display-state/reveal`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
    } catch (err) {
      console.error('Failed to reveal on display:', err);
    }
  };

  const showRoundAnswersOnDisplay = async () => {
    const roundContext = selectedRound || (selectedEvent && getEventType(selectedEvent) === 'theme-night'
      ? {
        id: THEME_NIGHT_ROUND_ID,
        name: selectedEvent.name || 'Thema-avond',
        order: 1,
        songs: selectedEvent.themeSongs || [],
        hitsterVariant: 'classic' as HitsterRoundVariant,
      }
      : null);
    if (!roundContext) return;
    const songs = [...roundContext.songs]
      .sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(s => ({
        id: s.id,
        order: s.order,
        title: s.title,
        artist: s.artist,
        year: s.year,
        imageUrl: s.imageUrl,
      }));
    try {
      await fetch(`${API_BASE}/api/display-state/round-answers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ songs, roundName: roundContext.name, startYear: roundContext.startYear ?? null })
      });
      setIsRoundAnswersActive(true);
      setIsIntermissionActive(false);
      setIsScoreboardActive(false);
    } catch (err) {
      console.error('Failed to show round answers on display:', err);
    }
  };

  const hideRoundAnswersOnDisplay = async () => {
    try {
      await fetch(`${API_BASE}/api/display-state/round-answers/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      setIsRoundAnswersActive(false);
    } catch (err) {
      console.error('Failed to hide round answers on display:', err);
    }
  };

  const stopDisplay = async () => {
    try {
      stopPlayAllRounds();

      const currentSongOrder = liveSongOrderRef.current;
      const roundSongCount = liveRoundSongCountRef.current;
      const liveRoundOrder = liveRoundOrderRef.current;
      const queuedSelection = queuedDisplaySelectionRef.current;
      const nextSongOrder = currentSongOrder && roundSongCount && currentSongOrder < roundSongCount
        ? currentSongOrder + 1
        : null;

      const automaticNextRound = selectedEvent?.rounds.find((round, roundIndex) =>
        (round.order || roundIndex + 1) === liveRoundOrder
      );
      const automaticNextSong = automaticNextRound?.songs.find(song => song.order === nextSongOrder);
      const stopPayload = queuedSelection
        ? {
            currentSongOrder: queuedSelection.songOrder,
            waitingMessage: null,
            roundName: queuedSelection.roundName,
            roundNumber: queuedSelection.roundNumber,
            hitsterGenerationPeriodId: queuedSelection.hitsterGenerationPeriodId || null,
            hitsterGenerationPeriodLabel: queuedSelection.hitsterGenerationPeriodLabel || null,
            hitsterGenerationPeriodIconUrl: queuedSelection.hitsterGenerationPeriodIconUrl || null
          }
        : {
            currentSongOrder: nextSongOrder,
            waitingMessage: nextSongOrder ? null : 'Ronde afgelopen'
          };

      await fetch(`${API_BASE}/api/display-state/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stopPayload)
      });
      setLiveSongId(null);
      liveSongIdRef.current = null;
      liveSongOrderRef.current = null;
      liveRoundSongCountRef.current = null;
      liveRoundOrderRef.current = null;
      setIsIntermissionActive(false);
      setIsRoundAnswersActive(false);
      setIsYoutubePaused(false); // Reset pause state
      if (queuedSelection) {
        setArmedDisplaySongId(queuedSelection.songId);
      } else if (automaticNextSong) {
        queuedDisplaySelectionRef.current = {
          songId: automaticNextSong.id,
          songOrder: automaticNextSong.order,
          roundNumber: automaticNextRound?.order || liveRoundOrder || 1,
          roundName: automaticNextRound?.name || '',
          hitsterGenerationPeriodId: null,
          hitsterGenerationPeriodLabel: null,
          hitsterGenerationPeriodIconUrl: null
        };
        setArmedDisplaySongId(automaticNextSong.id);
      } else {
        queuedDisplaySelectionRef.current = null;
        setArmedDisplaySongId(null);
      }
      // Stop any audio playback
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingYouTubeId(null);
      setPlayingSongId(null);
      // Stop Spotify if playing
      await pause();
      // Reset volume to 100% for next song
      await setVolume(1);
    } catch (err) {
      console.error('Failed to stop display:', err);
    }
  };
  // Fade out function - gradually decrease volume over duration
  const fadeOutDisplay = async (durationMs: number = 3000) => {
    if (isFading) return;
    void pushLog('info', `🔉 Fade gestart (${(durationMs / 1000).toFixed(1)}s)`, { durationMs });
    setIsFading(true);
    const steps = 24;
    const stepDuration = durationMs / steps;
    const displayVolumeStep = 100 / steps;

    // Do not depend on selectedRound lookup; that can be stale and skip fade.
    // Fade whatever live output is currently active.
    const shouldFadeDisplay = !!liveSongId;
    const shouldFadeAudio = !!audioRef.current;

    try {
      const initialAudioVolume = audioRef.current?.volume ?? 1;
      const audioVolumeStep = initialAudioVolume / steps;

      for (let i = 0; i < steps; i++) {
        await new Promise(resolve => setTimeout(resolve, stepDuration));

        const updates: Promise<any>[] = [];

        if (shouldFadeAudio && audioRef.current) {
          audioRef.current.volume = Math.max(0, initialAudioVolume - (audioVolumeStep * (i + 1)));
        }

        if (shouldFadeDisplay) {
          const newVolume = Math.max(0, 100 - (displayVolumeStep * (i + 1)));
          updates.push(
            fetch(`${API_BASE}/api/display-state/volume`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ volume: Math.round(newVolume) })
            })
          );
        }

        if (updates.length > 0) {
          await Promise.all(updates);
        }
      }

      // After fade, stop everything
      await stopDisplay();
      void pushLog('ok', '🔇 Fade voltooid, display gestopt');

      if (audioRef.current) {
        audioRef.current.volume = 1;
      }
      // Reset YouTube volume
      await fetch(`${API_BASE}/api/display-state/volume`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ volume: 100 })
      });
    } catch (err) {
      void pushLog('error', '❌ Fout tijdens fade', { error: String(err) });
      console.error('Fade out error:', err);
    } finally {
      setIsFading(false);
    }
  };

  const fadeDurationLabel = Number.isInteger(fadeDurationSeconds)
    ? `${fadeDurationSeconds}s`
    : `${fadeDurationSeconds.toFixed(1)}s`;

  const handleFadeDurationChange = (value: number) => {
    const clamped = Math.min(10, Math.max(1, value));
    setFadeDurationSeconds(Math.round(clamped * 2) / 2);
  };

  const triggerFadeOut = () => {
    fadeOutDisplay(fadeDurationSeconds * 1000);
  };

  // Toggle pause/play for display (works for all audio types)
  const toggleYoutubePause = async () => {
    const newPaused = !isYoutubePaused;
    setIsYoutubePaused(newPaused);
    
    // Get current song to check type
    const currentSong = selectedRound?.songs.find(s => s.id === liveSongId);
    
    try {
      // Update display state for YouTube/audio pause
      await fetch(`${API_BASE}/api/display-state/pause`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ paused: newPaused })
      });
      
      // Also control Spotify if this is a Spotify song
      if (currentSong?.spotifyUri && isAuthenticated && isPremium && isReady) {
        if (newPaused) {
          await pause();
        } else {
          await play(currentSong.spotifyUri);
        }
      }
    } catch (err) {
      console.error('Failed to toggle pause:', err);
    }
  };

  const toggleIntermissionScreen = async () => {
    const nextActive = !isIntermissionActive;

    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }

      if (overviewAudioRef.current) {
        overviewAudioRef.current.pause();
        overviewAudioRef.current = null;
      }

      setPlayingYouTubeId(null);
      setPlayingSongId(null);
      setOverviewPlayingSongId(null);

      try {
        await pause();
      } catch (pauseErr) {
        console.warn('Intermission: local Spotify pause skipped:', pauseErr);
      }

      await fetch(`${API_BASE}/api/display-state/intermission`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          active: nextActive,
          message: selectedEvent?.intermissionMessage || 'Tijd voor een drankje aan de bar'
        })
      });

      setIsIntermissionActive(nextActive);
      setLiveSongId(null);
      setIsYoutubePaused(false);
    } catch (err) {
      console.error('Failed to toggle intermission screen:', err);
    }
  };

  const showScoreboardOnDisplay = async () => {
    if (!selectedEvent?.id) return;
    try {
      await fetch(`${API_BASE}/api/display-state/scoreboard`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId: selectedEvent.id })
      });
      setIsScoreboardActive(true);
      setIsIntermissionActive(false);
      setLiveSongId(null);
    } catch (err) {
      console.error('Failed to show scoreboard:', err);
    }
  };

  const hideScoreboardOnDisplay = async () => {
    try {
      await fetch(`${API_BASE}/api/display-state/scoreboard/hide`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      setIsScoreboardActive(false);
    } catch (err) {
      console.error('Failed to hide scoreboard:', err);
    }
  };

  // Lyrics editing functions
  const openLyricsEditor = (song: EventSong) => {
    setShowSongOverview(false); // Close overview modal first
    setEditingLyricsSongId(song.id);
    setEditingLyricsSong(song);
    setLyricsText(song.lyrics || '');
    setLyricsPlaybackPosition(0);
    setLyricsIsPlaying(false);
    setLyricsPreviewMode(false);
  };
  const closeLyricsEditor = async () => {
    // Stop any playing audio
    if (lyricsAudioRef.current) {
      lyricsAudioRef.current.pause();
      lyricsAudioRef.current = null;
    }
    // Stop Spotify if playing
    if (lyricsIsPlaying && editingLyricsSong?.spotifyUri) {
      await pause();
    }
    setEditingLyricsSongId(null);
    setEditingLyricsSong(null);
    setLyricsIsPlaying(false);
    setLyricsPlaybackPosition(0);
  };
  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };
  const toggleLyricsPlayback = async () => {
    if (!editingLyricsSong) return;
    // If already playing, pause
    if (lyricsIsPlaying && lyricsAudioRef.current) {
      lyricsAudioRef.current.pause();
      setLyricsIsPlaying(false);
      return;
    }
    // If Spotify is playing, pause it
    if (lyricsIsPlaying && editingLyricsSong.spotifyUri) {
      await pause();
      setLyricsIsPlaying(false);
      return;
    }
    // Try to play via Spotify
    if (editingLyricsSong.spotifyUri && isAuthenticated && isPremium && isReady) {
      if (lyricsPlaybackPosition > 0) {
        // Resume from current position
        await resume();
      } else {
        await play(editingLyricsSong.spotifyUri);
      }
      setLyricsIsPlaying(true);
      return;
    }
    // Try to play via audio URL
    const audioUrl = editingLyricsSong.audioUrl || editingLyricsSong.previewUrl;
    if (audioUrl) {
      if (!lyricsAudioRef.current) {
        lyricsAudioRef.current = new Audio(audioUrl);
        lyricsAudioRef.current.ontimeupdate = () => {
          if (lyricsAudioRef.current) {
            setLyricsPlaybackPosition(lyricsAudioRef.current.currentTime * 1000);
          }
        };
        lyricsAudioRef.current.onended = () => {
          setLyricsIsPlaying(false);
          setLyricsPlaybackPosition(0);
        };
      }
      await lyricsAudioRef.current.play();
      setLyricsIsPlaying(true);
    }
  };
  toggleLyricsPlaybackRef.current = toggleLyricsPlayback;
  const insertTimestamp = () => {
    const timestamp = formatTime(lyricsPlaybackPosition);
    const textarea = lyricsTextareaRef.current;
    if (!textarea) return;
    const cursorPos = textarea.selectionStart;
    
    // Find the start of the current line
    const lineStart = lyricsText.lastIndexOf('\n', cursorPos - 1) + 1;
    
    // Find the end of the current line (start of next line)
    let lineEnd = lyricsText.indexOf('\n', cursorPos);
    if (lineEnd === -1) lineEnd = lyricsText.length;
    
    // Get text before line start, the current line, and after
    const before = lyricsText.substring(0, lineStart);
    const currentLine = lyricsText.substring(lineStart, lineEnd);
    const after = lyricsText.substring(lineEnd);
    
    // Remove any existing timestamp from the line
    const lineWithoutTimestamp = currentLine.replace(/^\[\d+:\d{2}\]\s*/, '');
    
    // Insert timestamp at the beginning of the current line
    const newText = `${before}[${timestamp}] ${lineWithoutTimestamp}${after}`;
    setLyricsText(newText);
    
    // Calculate cursor position for next non-empty line
    const newLineLength = `[${timestamp}] ${lineWithoutTimestamp}`.length;
    let pos = lineStart + newLineLength;
    // Advance past the current line's newline
    if (pos < newText.length && newText[pos] === '\n') pos++;
    // Skip empty lines
    while (pos < newText.length) {
      const nextNl = newText.indexOf('\n', pos);
      const nextLineEnd = nextNl === -1 ? newText.length : nextNl;
      const lineContent = newText.substring(pos, nextLineEnd);
      if (lineContent.trim() !== '') break; // found non-empty line
      pos = nextLineEnd + 1; // skip past this empty line's newline
    }
    if (pos > newText.length) pos = newText.length;
    // Store position; useEffect will apply it after React re-renders the textarea
    lyricsCursorRef.current = pos;
  };
  insertTimestampRef.current = insertTimestamp;

  const seekLyrics = async (positionMs: number) => {
    setLyricsPlaybackPosition(positionMs);
    if (lyricsAudioRef.current) {
      lyricsAudioRef.current.currentTime = positionMs / 1000;
    } else if (lyricsIsPlaying && editingLyricsSong?.spotifyUri) {
      await seek(positionMs);
    }
  };

  const saveLyrics = async () => {
    if (!selectedEvent || !selectedRound || !editingLyricsSongId) return;
    const updatedRounds = selectedEvent.rounds.map(round => {
      if (round.id === selectedRound.id) {
        return {
          ...round,
          songs: round.songs.map(song => 
            song.id === editingLyricsSongId 
              ? { ...song, lyrics: lyricsText }
              : song
          )
        };
      }
      return round;
    });
    const updatedEvent = { ...selectedEvent, rounds: updatedRounds };
    setSelectedEvent(updatedEvent);
    setSelectedRound(updatedRounds.find(r => r.id === selectedRound.id) || null);
    // Save to server
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent)
      });
    } catch (err) {
      console.error('Failed to save lyrics:', err);
    }
    setLyricsSaved(true);
    setTimeout(() => setLyricsSaved(false), 2000);
  };

  // Shift all timestamps in lyrics by deltaSeconds (positive = later, negative = earlier)
  const shiftAllTimestamps = (deltaSeconds: number) => {
    setLyricsText(prev =>
      prev.replace(/\[(\d+):(\d{2})\]/g, (_, m, s) => {
        const totalSecs = parseInt(m) * 60 + parseInt(s) + deltaSeconds;
        const clamped = Math.max(0, totalSecs);
        const mins = Math.floor(clamped / 60);
        const secs = Math.round(clamped % 60);
        return `[${mins}:${secs.toString().padStart(2, '0')}]`;
      })
    );
  };

  // Parse lyrics for preview panel
  const parseLyricsForPreview = (text: string): Array<{timestamp: number; text: string}> => {
    const result: Array<{timestamp: number; text: string}> = [];
    text.split('\n').forEach(line => {
      const match = line.match(/^\[(\d+):(\d{2})\]\s*(.*)/);
      if (match && match[3].trim()) {
        result.push({ timestamp: parseInt(match[1]) * 60000 + parseInt(match[2]) * 1000, text: match[3] });
      }
    });
    return result;
  };

  // Search lyrics automatically
  const searchLyricsAuto = async () => {
    if (!editingLyricsSong) return;
    
    setLyricsSearching(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/lyrics?artist=${encodeURIComponent(editingLyricsSong.artist)}&title=${encodeURIComponent(editingLyricsSong.title)}`
      );
      
      if (response.ok) {
        const data = await response.json();
        if (data.lyrics) {
          setLyricsText(data.lyrics);
        }
      } else {
        alert('Songtekst niet gevonden. Probeer handmatig in te voeren.');
      }
    } catch (err) {
      console.error('Failed to search lyrics:', err);
      alert('Fout bij ophalen songtekst');
    } finally {
      setLyricsSearching(false);
    }
  };

  // YouTube timing editing functions
  const parseTimeToSeconds = (timeStr: string): number => {
    if (!timeStr.trim()) return 0;
    const parts = timeStr.split(':').map(p => parseInt(p.trim(), 10));
    if (parts.length === 2) {
      // m:ss format
      return (parts[0] * 60) + parts[1];
    } else if (parts.length === 1) {
      // Just seconds
      return parts[0];
    }
    return 0;
  };
  const formatSecondsToTime = (seconds: number | undefined): string => {
    if (!seconds) return '';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  const openYoutubeTimingEditor = (song: EventSong) => {
    setEditingYoutubeTimingSongId(song.id);
    setEditingYoutubeTimingSong(song);
    setYoutubeStartTimeInput(formatSecondsToTime(song.youtubeStartTime));
    setYoutubeEndTimeInput(formatSecondsToTime(song.youtubeEndTime));
    setAudioStartTimeInput(formatSecondsToTime(song.audioStartTime));
    setVolumeGainInput(Math.round((song.volumeGain ?? 1.0) * 100));
  };
  const closeYoutubeTimingEditor = () => {
    if (ytTimingPlayerRef.current) {
      ytTimingPlayerRef.current.destroy();
      ytTimingPlayerRef.current = null;
    }
    setEditingYoutubeTimingSongId(null);
    setEditingYoutubeTimingSong(null);
    setYoutubeStartTimeInput('');
    setYoutubeEndTimeInput('');
    setAudioStartTimeInput('');
    setVolumeGainInput(100);
  };

  // Grab current YouTube position as start time
  const grabYoutubeStartTime = () => {
    if (ytTimingPlayerRef.current) {
      const currentTime = ytTimingPlayerRef.current.getCurrentTime();
      setYoutubeStartTimeInput(formatSecondsToTime(Math.floor(currentTime)));
    }
  };

  // Grab current YouTube position as end time
  const grabYoutubeEndTime = () => {
    if (ytTimingPlayerRef.current) {
      const currentTime = ytTimingPlayerRef.current.getCurrentTime();
      setYoutubeEndTimeInput(formatSecondsToTime(Math.floor(currentTime)));
    }
  };

  // Preview the selected fragment
  const previewYoutubeFragment = () => {
    if (ytTimingPlayerRef.current && youtubeStartTimeInput) {
      const startSeconds = parseTimeToSeconds(youtubeStartTimeInput);
      ytTimingPlayerRef.current.seekTo(startSeconds, true);
      ytTimingPlayerRef.current.playVideo();
    }
  };

  const saveYoutubeTiming = async () => {
    if (!selectedEvent || !selectedRound || !editingYoutubeTimingSongId) return;
    const startTime = parseTimeToSeconds(youtubeStartTimeInput);
    const endTime = parseTimeToSeconds(youtubeEndTimeInput);
    const audioStart = parseTimeToSeconds(audioStartTimeInput);
    const volumeGain = volumeGainInput / 100; // Convert percentage to multiplier
    const updatedRounds = selectedEvent.rounds.map(round => {
      if (round.id === selectedRound.id) {
        return {
          ...round,
          songs: round.songs.map(song => 
            song.id === editingYoutubeTimingSongId 
              ? { 
                  ...song, 
                  youtubeStartTime: startTime || undefined, 
                  youtubeEndTime: endTime || undefined,
                  audioStartTime: audioStart || undefined,
                  volumeGain: volumeGain !== 1.0 ? volumeGain : undefined
                }
              : song
          )
        };
      }
      return round;
    });
    const updatedEvent = { ...selectedEvent, rounds: updatedRounds };
    setSelectedEvent(updatedEvent);
    setSelectedRound(updatedRounds.find(r => r.id === selectedRound.id) || null);
    // Save to server
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent)
      });
    } catch (err) {
      console.error('Failed to save song settings:', err);
    }
    closeYoutubeTimingEditor();
  };
  // Update song from Discogs verification
  // Quick volume adjustment for songs
  const adjustSongVolume = async (songId: string, volumePercent: number) => {
    if (!selectedEvent || !selectedRound) return;
    
    const clamped = Math.max(10, Math.min(200, volumePercent));
    const volumeGain = clamped / 100;
    
    const updatedRounds = selectedEvent.rounds.map(round => {
      if (round.id === selectedRound.id) {
        return {
          ...round,
          songs: round.songs.map(song => 
            song.id === songId 
              ? { ...song, volumeGain: volumeGain !== 1.0 ? volumeGain : undefined }
              : song
          )
        };
      }
      return round;
    });
    
    const updatedEvent = { ...selectedEvent, rounds: updatedRounds };
    setSelectedEvent(updatedEvent);
    setSelectedRound(updatedRounds.find(r => r.id === selectedRound.id) || null);
    
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent)
      });
    } catch (err) {
      console.error('Failed to save volume:', err);
    }
  };

  const updateSongFromDiscogs = async (songId: string, updates: { year?: number; title?: string; artist?: string; yearSource?: string }) => {
    if (!selectedEvent || !selectedRound) return;
    const updatedRounds = selectedEvent.rounds.map(round => {
      if (round.id === selectedRound.id) {
        return {
          ...round,
          songs: round.songs.map(song => 
            song.id === songId 
              ? { ...song, ...updates }
              : song
          )
        };
      }
      return round;
    });
    const updatedEvent = { ...selectedEvent, rounds: updatedRounds };
    setSelectedEvent(updatedEvent);
    setSelectedRound(updatedRounds.find(r => r.id === selectedRound.id) || null);
    // Save to server
    try {
      await fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent)
      });
    } catch (err) {
      console.error('Failed to save Discogs update:', err);
    }
  };
  // YouTube search functions
  const mapMediaApiError = (status?: number, apiError?: string) => {
    const message = apiError || '';

    if (status === 422 || /not available|video unavailable|private video|niet beschikbaar|niet openbaar/i.test(message)) {
      return 'Deze video is niet beschikbaar of niet openbaar.';
    }

    if (status === 400 || /unsupported|niet ondersteund|ongeldige url|invalid url/i.test(message)) {
      return 'Deze URL wordt niet ondersteund of is ongeldig.';
    }

    if (status === 500) {
      return 'Serverfout bij video ophalen. Probeer een andere video of opnieuw later.';
    }

    return message || 'Onbekende fout bij video verwerken.';
  };

  const handleYouTubeSearch = async () => {
    const trimmedQuery = ytSearchQuery.trim();
    if (!trimmedQuery) return;
    const isDirectUrl = /^https?:\/\//i.test(trimmedQuery);

    setYtIsSearching(true);
    setYtDownloadStatus('');
    setError(null);
    setYtResults([]);
    setYtSelectedVideo(null);

    if (isDirectUrl) {
      setYtIsLoadingInfo(true);
      try {
        const response = await fetch(`${API_BASE}/api/youtube/info?url=${encodeURIComponent(trimmedQuery)}`);
        const data = await response.json().catch(() => ({}));
        if (!response.ok || data.error) {
          const message = mapMediaApiError(response.status, data.error);
          setError(message);
          setYtDownloadStatus(`⚠️ ${message}`);
        } else {
          setYtSelectedVideo({ ...data, sourceUrl: trimmedQuery });
          setYtEditTitle(data.title || '');
          setYtEditArtist(data.artist || data.channel || '');
          setYtEditYear(data.year?.toString() || new Date().getFullYear().toString());
          setYtDownloadStatus('');
        }
      } catch (err) {
        const message = 'Video informatie ophalen mislukt. Controleer de URL en serverstatus.';
        setError(message);
        setYtDownloadStatus(`⚠️ ${message}`);
      } finally {
        setYtIsLoadingInfo(false);
        setYtIsSearching(false);
      }
      return;
    }

    try {
      const response = await fetch(`${API_BASE}/api/youtube/search?query=${encodeURIComponent(trimmedQuery)}`);
      const data = await response.json();
      if (data.error) {
        setError(data.error);
      } else {
        setYtResults(data.results || []);
      }
    } catch (err) {
      setError('Zoeken mislukt. Is de server actief?');
    } finally {
      setYtIsSearching(false);
    }
  };
  const handleYouTubeSelectVideo = async (video: YouTubeResult) => {
    setYtIsLoadingInfo(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE}/api/youtube/info?url=${encodeURIComponent(video.url)}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        // Use basic info from search result
        setYtSelectedVideo({
          id: video.id,
          title: video.title,
          artist: video.channel,
          channel: video.channel,
          duration: video.duration,
          thumbnail: video.thumbnail,
          year: new Date().getFullYear(),
          sourceUrl: video.url
        });
        setError(mapMediaApiError(response.status, data.error));
        setYtEditTitle(video.title);
        setYtEditArtist(video.channel);
        setYtEditYear(new Date().getFullYear().toString());
      } else {
        setYtSelectedVideo({ ...data, sourceUrl: video.url });
        setYtEditTitle(data.title || '');
        setYtEditArtist(data.artist || data.channel || '');
        setYtEditYear(data.year?.toString() || new Date().getFullYear().toString());
      }
    } catch (err) {
      // Use basic info from search result
      setYtSelectedVideo({
        id: video.id,
        title: video.title,
        artist: video.channel,
        channel: video.channel,
        duration: video.duration,
        thumbnail: video.thumbnail,
        year: new Date().getFullYear(),
        sourceUrl: video.url
      });
      setYtEditTitle(video.title);
      setYtEditArtist(video.channel);
      setYtEditYear(new Date().getFullYear().toString());
    } finally {
      setYtIsLoadingInfo(false);
    }
  };
  const handleYouTubeDownload = async () => {
    if (!ytSelectedVideo || !selectedEvent) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const isThemeNight = getEventType(selectedEvent) === 'theme-night';
    const targetSongs = selectedRound?.songs || (isThemeNight ? (selectedEvent.themeSongs || []) : null);
    if (!targetSongs) {
      setError('Selecteer eerst een ronde of thema-avond');
      return;
    }
    setYtIsDownloading(true);
    setError(null);
    setYtDownloadStatus('Audio downloaden...');

    const selectedUrl = ytSelectedVideo.sourceUrl || `https://www.youtube.com/watch?v=${ytSelectedVideo.id}`;

    try {
      const response = await fetch(`${API_BASE}/api/youtube/download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          url: selectedUrl,
          title: ytEditTitle
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        setError(`Download mislukt: ${mapMediaApiError(response.status, data.error)}`);
        setYtDownloadStatus('');
      } else {
        setYtDownloadStatus('Download compleet! Toevoegen...');
        // Add to selected round or theme-night
        const song: EventSong = {
          id: `song-${Date.now()}`,
          title: ytEditTitle,
          artist: ytEditArtist,
          year: parseInt(ytEditYear) || new Date().getFullYear(),
          duration: ytSelectedVideo.duration,
          imageUrl: ytSelectedVideo.thumbnail,
          audioUrl: data.url,
          youtubeVideoId: ytSelectedVideo.id,
          order: targetSongs.length + 1,
        };

        let updatedRound: EventRound | null = null;
        let updatedEvent: HitsterEvent;

        if (selectedRound) {
          updatedRound = {
            ...selectedRound,
            songs: [...selectedRound.songs, song],
          };
          updatedEvent = {
            ...selectedEvent,
            rounds: selectedEvent.rounds.map(r => r.id === updatedRound!.id ? updatedRound! : r),
            updatedAt: new Date().toISOString(),
          };
        } else {
          updatedEvent = {
            ...selectedEvent,
            themeSongs: [...(selectedEvent.themeSongs || []), song],
            updatedAt: new Date().toISOString(),
          };
        }

        // Save to server
        fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(updatedEvent),
        }).catch(err => console.error('Error saving:', err));

        if (updatedRound) {
          setSelectedRound(updatedRound);
        }
        setSelectedEvent(updatedEvent);
        setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
        // Reset state
        setYtSelectedVideo(null);
        setYtDownloadStatus('✓ Nummer toegevoegd!');
        setTimeout(() => setYtDownloadStatus(''), 3000);
      }
    } catch (err) {
      setError('Download mislukt. Controleer of yt-dlp is geïnstalleerd.');
      setYtDownloadStatus('');
    } finally {
      setYtIsDownloading(false);
    }
  };
  // Downloaded files functions
  const loadDownloadedFiles = async () => {
    setDownloadsLoading(true);
    try {
      const response = await fetch(`${API_BASE}/api/downloads`);
      if (response.ok) {
        const data = await response.json();
        // Parse filename to extract title, artist, year
        const parsedFiles = data.files.map((file: { filename: string; url: string; size: number }) => {
          // Expected format: "Artist - Title (Year).ext" or just filename
          // Remove audio extension
          const nameWithoutExt = file.filename.replace(/\.(mp3|m4a|webm|opus|ogg|wav|flac|aac)$/i, '');
          const yearMatch = nameWithoutExt.match(/\((\d{4})\)$/);
          const year = yearMatch ? parseInt(yearMatch[1]) : undefined;
          const nameWithoutYear = yearMatch ? nameWithoutExt.replace(/\s*\(\d{4}\)$/, '') : nameWithoutExt;
          
          const parts = nameWithoutYear.split(' - ');
          let artist = '', title = '';
          if (parts.length >= 2) {
            artist = parts[0].trim();
            title = parts.slice(1).join(' - ').trim();
          } else {
            title = nameWithoutYear;
          }
          
          return {
            ...file,
            title,
            artist,
            year
          };
        });
        setDownloadedFiles(parsedFiles);
      }
    } catch (err) {
      console.error('Error loading downloads:', err);
    } finally {
      setDownloadsLoading(false);
    }
  };
  const playDownloadPreview = (file: DownloadedFile) => {
    if (playingDownloadUrl === file.url) {
      // Stop playing
      if (downloadAudioRef.current) {
        downloadAudioRef.current.pause();
        downloadAudioRef.current = null;
      }
      setPlayingDownloadUrl(null);
    } else {
      // Stop any current audio
      if (downloadAudioRef.current) {
        downloadAudioRef.current.pause();
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      setPlayingSongId(null);
      
      // Play this file
      const audio = new Audio(`${API_BASE}${file.url}`);
      audio.onended = () => setPlayingDownloadUrl(null);
      audio.play();
      downloadAudioRef.current = audio;
      setPlayingDownloadUrl(file.url);
    }
  };
  const selectDownloadForEditing = (file: DownloadedFile) => {
    setEditingDownload(file);
    setDlEditTitle(file.title || '');
    setDlEditArtist(file.artist || '');
    setDlEditYear(file.year?.toString() || new Date().getFullYear().toString());
  };
  const quickAddDownloadToRound = (file: DownloadedFile) => {
    if (!selectedEvent) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const isThemeNight = getEventType(selectedEvent) === 'theme-night';
    const targetSongs = selectedRound?.songs || (isThemeNight ? (selectedEvent.themeSongs || []) : null);
    if (!targetSongs) {
      setError('Selecteer eerst een ronde of thema-avond');
      return;
    }
    const title = file.title || file.filename;
    const artist = file.artist || 'Onbekend';
    const year = file.year || new Date().getFullYear();
    // Check if already added
    if (targetSongs.some(s => s.audioUrl === `${API_BASE}${file.url}`)) {
      setError('Dit nummer staat al in de lijst');
      return;
    }
    const song: EventSong = {
      id: `song-${Date.now()}`,
      title,
      artist,
      album: 'Download',
      year,
      duration: 0,
      audioUrl: `${API_BASE}${file.url}`,
      order: targetSongs.length + 1,
    };

    let updatedRound: EventRound | null = null;
    let updatedEvent: HitsterEvent;

    if (selectedRound) {
      updatedRound = {
        ...selectedRound,
        songs: [...selectedRound.songs, song],
      };
      updatedEvent = {
        ...selectedEvent,
        rounds: selectedEvent.rounds.map(r => r.id === updatedRound!.id ? updatedRound! : r),
        updatedAt: new Date().toISOString(),
      };
    } else {
      updatedEvent = {
        ...selectedEvent,
        themeSongs: [...(selectedEvent.themeSongs || []), song],
        updatedAt: new Date().toISOString(),
      };
    }

    // Save to server
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));

    if (updatedRound) {
      setSelectedRound(updatedRound);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setError(null);
  };
  const addDownloadedFileToRound = () => {
    if (!selectedEvent || !editingDownload) {
      setError('Selecteer eerst een evenement');
      return;
    }

    const isThemeNight = getEventType(selectedEvent) === 'theme-night';
    const targetSongs = selectedRound?.songs || (isThemeNight ? (selectedEvent.themeSongs || []) : null);
    if (!targetSongs) {
      setError('Selecteer eerst een ronde of thema-avond');
      return;
    }
    if (!dlEditTitle || !dlEditArtist || !dlEditYear) {
      setError('Vul alle velden in');
      return;
    }
    // Check if already added
    if (targetSongs.some(s => s.audioUrl === `${API_BASE}${editingDownload.url}`)) {
      setError('Dit nummer staat al in de lijst');
      return;
    }
    const song: EventSong = {
      id: `song-${Date.now()}`,
      title: dlEditTitle,
      artist: dlEditArtist,
      album: 'Download',
      year: parseInt(dlEditYear) || new Date().getFullYear(),
      duration: 0,
      audioUrl: `${API_BASE}${editingDownload.url}`,
      order: targetSongs.length + 1,
    };

    let updatedRound: EventRound | null = null;
    let updatedEvent: HitsterEvent;

    if (selectedRound) {
      updatedRound = {
        ...selectedRound,
        songs: [...selectedRound.songs, song],
      };
      updatedEvent = {
        ...selectedEvent,
        rounds: selectedEvent.rounds.map(r => r.id === updatedRound!.id ? updatedRound! : r),
        updatedAt: new Date().toISOString(),
      };
    } else {
      updatedEvent = {
        ...selectedEvent,
        themeSongs: [...(selectedEvent.themeSongs || []), song],
        updatedAt: new Date().toISOString(),
      };
    }

    // Save to server
    fetch(`${API_BASE}/api/events/${selectedEvent.id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(updatedEvent),
    }).catch(err => console.error('Error saving:', err));

    if (updatedRound) {
      setSelectedRound(updatedRound);
    }
    setSelectedEvent(updatedEvent);
    setEvents(events.map(e => e.id === updatedEvent.id ? updatedEvent : e));
    setEditingDownload(null);
    setError(null);
  };
  const deleteDownloadedFile = async (filename: string) => {
    if (!window.confirm(`Weet je zeker dat je "${filename}" wilt verwijderen?`)) {
      return;
    }
    
    try {
      const response = await fetch(`${API_BASE}/api/downloads/${encodeURIComponent(filename)}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        setDownloadedFiles(downloadedFiles.filter(f => f.filename !== filename));
        if (editingDownload?.filename === filename) {
          setEditingDownload(null);
        }
      }
    } catch (err) {
      console.error('Error deleting file:', err);
    }
  };
  // Load downloads when mode changes
  useEffect(() => {
    if (searchMode === 'downloads') {
      loadDownloadedFiles();
    }
  }, [searchMode]);

  useEffect(() => {
    setOverviewGenerationFilter('all');
    setRightPanelGenerationFilter('all');
  }, [selectedRound?.id]);
  // QR Scanner functions
  const startQrScanner = () => {
    if (!selectedRound) {
      setError('Selecteer eerst een ronde om nummers aan toe te voegen');
      return;
    }
    setShowQrScanner(true);
    setQrScannerError(null);
  };
  const stopQrScanner = () => {
    if (qrScannerRef.current) {
      qrScannerRef.current.clear().catch(console.error);
      qrScannerRef.current = null;
    }
    setShowQrScanner(false);
    setQrProcessing(false);
  };
  // Process scanned QR URL - defined before useEffect to avoid closure issues
  const processScannedUrl = async (url: string) => {
    if (qrProcessing) return;
    setQrProcessing(true);
    setQrScannerError(null);
    
    console.log('QR scanned URL:', url);
    try {
      // Parse Spotify URLs
      // Formats: 
      // - https://open.spotify.com/track/TRACK_ID
      // - spotify:track:TRACK_ID
      const spotifyTrackRegex = /(?:spotify:track:|open\.spotify\.com\/track\/)([a-zA-Z0-9]+)/;
      const spotifyMatch = url.match(spotifyTrackRegex);
      
      console.log('Spotify match:', spotifyMatch);
      if (spotifyMatch) {
        const trackId = spotifyMatch[1];
        console.log('Adding Spotify track:', trackId);
        await addSpotifyTrackByIdQr(trackId);
        stopQrScanner();
        return;
      }
      
      // Check for Hitster card URLs (hitstergame.com/xxx or hitster.app/xxx)
      const hitsterRegex = /(?:hitstergame\.com|hitster\.app|hitster\.nl)\/([a-zA-Z0-9]+)/i;
      const hitsterMatch = url.match(hitsterRegex);
      
      console.log('Hitster match:', hitsterMatch);
      
      if (hitsterMatch) {
        const cardCode = hitsterMatch[1];
        console.log('Hitster card code:', cardCode);
        setQrScannerError(`Hitster kaart: ${cardCode} - handmatig opzoeken nodig`);
        setQrProcessing(false);
        return;
      }
      // Parse YouTube URLs
      const youtubeRegex = /(?:youtube\.com\/watch\?v=|youtu\.be\/|music\.youtube\.com\/watch\?v=)([a-zA-Z0-9_-]+)/;
      const youtubeMatch = url.match(youtubeRegex);
      
      console.log('YouTube match:', youtubeMatch);
      if (youtubeMatch) {
        const videoId = youtubeMatch[1];
        console.log('Adding YouTube video:', videoId);
        await addYouTubeVideoByIdQr(videoId);
        stopQrScanner();
        return;
      }
      // Show the scanned URL if not recognized
      setQrScannerError(`Onbekend formaat: ${url.substring(0, 80)}`);
    } catch (err) {
      console.error('Error processing QR:', err);
      setQrScannerError('Fout bij verwerken van QR code');
    } finally {
      setQrProcessing(false);
    }
  };
  // Separate QR helper functions to avoid stale closure
  const addSpotifyTrackByIdQr = async (trackId: string) => {
    const event = selectedEvent;
    const round = selectedRound;
    if (!event || !round) return;
    if (round.songs.some(s => s.spotifyId === trackId)) {
      setQrScannerError('Dit nummer staat al in de ronde');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/spotify/track/${trackId}`);
      if (!response.ok) throw new Error('Track not found');
      
      const track = await response.json();
      const recommendedGain = typeof track.recommendedGain === 'number'
        ? track.recommendedGain
        : await getSpotifyRecommendedGain(track.id);
      const song: EventSong = {
        id: `song-${Date.now()}`,
        spotifyId: track.id,
        title: track.title,
        artist: track.artist,
        album: track.album,
        year: track.year || new Date().getFullYear(),
        duration: track.duration,
        imageUrl: track.thumbnail || undefined,
        previewUrl: track.previewUrl || undefined,
        spotifyUri: `spotify:track:${track.id}`,
        volumeGain: recommendedGain !== undefined && recommendedGain !== 1 ? recommendedGain : undefined,
        order: round.songs.length + 1,
      };
      const updatedRound = {
        ...round,
        songs: [...round.songs, song],
      };
      const updatedEvent = {
        ...event,
        rounds: event.rounds.map(r => r.id === updatedRound.id ? updatedRound : r),
        updatedAt: new Date().toISOString(),
      };
      await fetch(`${API_BASE}/api/events/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
      setSelectedRound(updatedRound);
      setSelectedEvent(updatedEvent);
      setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      setError(null);
    } catch (err) {
      console.error('Error adding Spotify track:', err);
      setQrScannerError('Kon Spotify track niet ophalen');
    }
  };
  const addYouTubeVideoByIdQr = async (videoId: string) => {
    const event = selectedEvent;
    const round = selectedRound;
    if (!event || !round) return;
    if (round.songs.some(s => s.youtubeVideoId === videoId)) {
      setQrScannerError('Deze video staat al in de ronde');
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/youtube/video/${videoId}`);
      if (!response.ok) throw new Error('Video not found');
      
      const video = await response.json();
      const song: EventSong = {
        id: `song-${Date.now()}`,
        youtubeVideoId: video.id,
        title: video.title,
        artist: video.artist || video.channel,
        year: video.year || new Date().getFullYear(),
        duration: video.duration,
        imageUrl: video.thumbnail || undefined,
        audioUrl: video.audioUrl || undefined,
        order: round.songs.length + 1,
      };
      const updatedRound = {
        ...round,
        songs: [...round.songs, song],
      };
      const updatedEvent = {
        ...event,
        rounds: event.rounds.map(r => r.id === updatedRound.id ? updatedRound : r),
        updatedAt: new Date().toISOString(),
      };
      await fetch(`${API_BASE}/api/events/${event.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updatedEvent),
      });
      setSelectedRound(updatedRound);
      setSelectedEvent(updatedEvent);
      setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      setError(null);
    } catch (err) {
      console.error('Error adding YouTube video:', err);
      setQrScannerError('Kon YouTube video niet ophalen');
    }
  };
  useEffect(() => {
    if (showQrScanner && !qrScannerRef.current) {
      const scanner = new Html5QrcodeScanner(
        'qr-reader',
        { 
          fps: 10, 
          qrbox: { width: 200, height: 200 },
          rememberLastUsedCamera: true,
          showTorchButtonIfSupported: true,
          showZoomSliderIfSupported: true,
          // Don't restrict formats - let it detect any QR/barcode
        },
        false
      );
      console.log('QR Scanner initialized, waiting for codes...');
      
      scanner.render(
        (decodedText: string) => {
          console.log('=== QR CODE DETECTED ===');
          console.log('Raw decoded text:', decodedText);
          console.log('Text length:', decodedText.length);
          console.log('========================');
          // Use setTimeout to break out of the scanner callback context
          setTimeout(() => processScannedUrl(decodedText), 0);
        },
        (errorMessage: string) => {
          // Only log non-standard errors (not "No QR code found")
          if (!errorMessage.includes('No MultiFormat Readers') && 
              !errorMessage.includes('No barcode or QR code detected') &&
              !errorMessage.includes('NotFoundException')) {
            console.log('QR Scan error:', errorMessage);
          }
        }
      );
      qrScannerRef.current = scanner;
    }
    return () => {
      if (qrScannerRef.current) {
        qrScannerRef.current.clear().catch(console.error);
        qrScannerRef.current = null;
      }
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showQrScanner, selectedEvent, selectedRound]);
  // Configuration needed
  const isBattleGenerationsRound = !!selectedRound && (selectedRound.hitsterVariant || 'classic') === 'battle-generations';
  const isThemeNightSelected = Boolean(selectedEvent && getEventType(selectedEvent) === 'theme-night');
  const selectedSongList = selectedRound?.songs || (isThemeNightSelected ? (selectedEvent?.themeSongs || []) : []);
  const rightPanelSongs = selectedRound
    ? (isBattleGenerationsRound
      ? filterSongsByGeneration(selectedRound.songs, rightPanelGenerationFilter)
      : selectedRound.songs)
    : (isThemeNightSelected ? selectedSongList : []);
  const overviewSongs = selectedRound
    ? (isBattleGenerationsRound
      ? filterSongsByGeneration(selectedRound.songs, overviewGenerationFilter)
      : selectedRound.songs)
    : (isThemeNightSelected ? selectedSongList : []);
  const canAddToSelectedList = Boolean(selectedEvent && (selectedRound || isThemeNightSelected));
  const selectedListLabel = selectedRound?.name || (isThemeNightSelected ? 'thema-avond' : 'ronde');
  const themeNightCurrentSetSongs = isThemeNightSelected
    ? themeNightSetSongIds
      .map((songId) => (selectedEvent?.themeSongs || []).find((song) => song.id === songId))
      .filter((song): song is EventSong => Boolean(song))
    : [];
  const highestThemeNightSetNumber = themeNightSetHistory.reduce((max, item) => {
    const parsed = getThemeNightSetNumberFromId(item.setId);
    return parsed ? Math.max(max, parsed) : max;
  }, 0);
  const activeThemeNightSetNumber = highestThemeNightSetNumber + 1;
  const activeThemeNightSetSlot = Math.min(themeNightSetSongIds.length + 1, 10);
  const themeNightSetNumbers = Array.from(
    new Set(
      themeNightSetHistory
        .map((item) => getThemeNightSetNumberFromId(item.setId))
        .filter((value): value is number => Boolean(value))
    )
  ).sort((a, b) => a - b);
  const getSongSetNumbers = (songId: string): number[] => themeNightSongSetMap[songId] || [];
  const getSongSetLabel = (songId: string): string => {
    const numbers = getSongSetNumbers(songId);
    if (numbers.length === 0) return '-';
    return numbers.map((n) => `Set ${n}`).join(', ');
  };
  const openThemeNightSetAnswers = async (setId: string, focus: 'answers' | 'teams' = 'answers') => {
    if (!selectedEvent) return;
    setThemeNightPanelSetId(setId);
    setThemeNightPanelFocus(focus);
    setThemeNightPanelLoading(true);
    setThemeNightPanelAnswerKeys([]);
    setThemeNightPanelBingoScores({});
    try {
      const [keysRes, bingoRes] = await Promise.all([
        fetch(`${API_BASE}/api/answer-keys/${selectedEvent.id}/${setId}`),
        fetch(`${API_BASE}/api/bingo/scores/${selectedEvent.id}/${setId}`),
      ]);
      if (keysRes.ok) {
        const data = await keysRes.json();
        const entries = Array.isArray(data) ? data : (data.entries || []);
        const sorted = [...entries].sort((a: any, b: any) => (a.order ?? 0) - (b.order ?? 0));
        setThemeNightPanelAnswerKeys(sorted);
      }
      if (bingoRes.ok) {
        const scores = await bingoRes.json();
        setThemeNightPanelBingoScores(scores);
      }
    } catch {
      // ignore load errors - panel shows empty state
    } finally {
      setThemeNightPanelLoading(false);
    }
  };
  const filteredOverviewSongs = (isThemeNightSelected && themeNightOverviewSetFilter !== 'all')
    ? overviewSongs.filter((song) => getSongSetNumbers(song.id).includes(Number(themeNightOverviewSetFilter)))
    : overviewSongs;
  const themeDisplayRound: EventRound | null = isThemeNightSelected && selectedEvent
    ? {
      id: 'theme-night',
      name: selectedEvent.name || 'Thema-avond',
      order: 1,
      songs: selectedEvent.themeSongs || [],
      hitsterVariant: 'classic',
    }
    : null;
  const overviewRoundContext = selectedRound || themeDisplayRound;

  if (isConfigured === false && !showConfig) {
    return (
      <div className="spotify-dashboard">
        <div className="dashboard-header">
          <div className="header-title-group">
            <h1>🎧 Music Dashboard</h1>
            <button className="back-btn" onClick={() => navigate('/prepare')}>
              ← Terug
            </button>
          </div>
        </div>
        <div className="config-needed">
          <h2>Spotify Configuratie Nodig</h2>
          <p>Om Spotify te gebruiken heb je API credentials nodig.</p>
          <ol>
            <li>Ga naar <a href="https://developer.spotify.com/dashboard" target="_blank" rel="noopener noreferrer">developer.spotify.com/dashboard</a></li>
            <li>Log in en maak een nieuwe app aan</li>
            <li>Kopieer de Client ID en Client Secret</li>
          </ol>
          <Button variant="primary" onClick={() => setShowConfig(true)}>
            🔧 Configureren
          </Button>
        </div>
      </div>
    );
  }
  // Configuration modal
  if (showConfig) {
    return (
      <div className="spotify-dashboard">
        <div className="dashboard-header">
          <button className="back-btn" onClick={() => setShowConfig(false)}>
            ← Terug
          </button>
          <h1>🔧 Spotify Configureren</h1>
        </div>
        <div className="config-modal">
          <div className="config-field">
            <label>Client ID</label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="Jouw Spotify Client ID"
            />
          </div>
          <div className="config-field">
            <label>Client Secret</label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              placeholder="Jouw Spotify Client Secret"
            />
          </div>
          {error && <p className="error">{error}</p>}
          <div className="config-actions">
            <Button variant="secondary" onClick={() => setShowConfig(false)}>
              Annuleren
            </Button>
            <Button variant="success" onClick={handleConfigure} disabled={!clientId || !clientSecret}>
              ✓ Opslaan
            </Button>
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="spotify-dashboard">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-title-group">
          <button className="back-btn" onClick={() => navigate('/prepare')}>
            ← Terug
          </button>
          <h1>🎧 Music Dashboard</h1>
        </div>
        <div className="header-actions">
          <button
            className="homepage-manage-btn"
            onClick={() => navigate('/homepage-beheer')}
            title="Beheer startpagina"
          >
            ✍️ Startpagina
          </button>
          <button
            className="home-btn"
            onClick={() => navigate('/admin-home')}
            title="Ga naar admin home"
          >
            🏠 Home
          </button>
          <button 
            className="bingo-btn"
            onClick={() => {
              let url = '/music-bingo?mode=hitster';
              if (selectedEvent && selectedRound) {
                url = `/music-bingo/${selectedEvent.id}/${selectedRound.id}?mode=hitster`;
              } else if (selectedEvent) {
                if (getEventType(selectedEvent) === 'theme-night') {
                  url = `/music-bingo/${selectedEvent.id}?mode=hitster`;
                } else {
                  const firstRound = [...(selectedEvent.rounds || [])]
                    .sort((left, right) => left.order - right.order)[0];
                  url = firstRound
                    ? `/music-bingo/${selectedEvent.id}/${firstRound.id}?mode=hitster`
                    : `/music-bingo/${selectedEvent.id}?mode=hitster`;
                }
              }
              navigate(url);
            }}
            title="Muziekbingo kaartjes genereren"
          >
            🎯 Bingo
          </button>
          <button 
            className="quiz-btn"
            onClick={() => navigate('/pubquiz')}
            title="Pub Quiz beheren"
          >
            🧠 Quiz
          </button>
          <button 
            className="lingo-btn"
            onClick={() => {
              let url = '/lingo';
              if (selectedEvent && selectedRound) {
                url = `/lingo/${selectedEvent.id}/${selectedRound.id}`;
              } else if (selectedEvent) {
                url = `/lingo/${selectedEvent.id}`;
              }
              navigate(url);
            }}
            title="Lingo - raad de artiest"
          >
            🔤 Lingo
          </button>
          <button
            className="murder-btn"
            onClick={() => window.open('https://wieisdedader.nl/fatal-attraction-online-moordspel.html', '_blank', 'noopener,noreferrer')}
            title="Open Fatal Attraction online moordspel"
          >
            🔪 Moordspel
          </button>
          <button 
            className="display-btn" 
            onClick={() => window.open('/display', '_blank')}
            title="Open Display in nieuw venster"
          >
            📺 Display
          </button>
          <button
            className="reactions-btn"
            onClick={() => window.open('/reacties-beheer', '_blank')}
            title="Open Reacties Beheer in nieuw venster"
          >
            💬 Reacties
          </button>
          <button className="config-btn" onClick={() => setShowConfig(true)}>
            ⚙️
          </button>
        </div>
      </div>
      <SpotifyPlayer />
      <div className="dashboard-layout">
        {/* Left Panel: Events & Rounds */}
        <div className="panel events-panel">
          {/* Show events list only when no event is selected */}
          {!selectedEvent && (
            <>
              <div 
                className="panel-header collapsible"
                onClick={() => setEventsCollapsed(!eventsCollapsed)}
              >
                <h2>
                  <span className={`collapse-icon ${eventsCollapsed ? 'collapsed' : ''}`}>▼</span>
                  📅 Evenementen
                </h2>
                <button className="add-btn" onClick={(e) => { e.stopPropagation(); setShowEventModal(true); }}>
                  + Nieuw
                </button>
              </div>
              {!eventsCollapsed && (
              <div className="events-list">
                {events.map(event => (
                  <div 
                    key={event.id}
                    className="event-card"
                    onClick={() => {
                      setSelectedEvent(event);
                      if (getEventType(event) === 'rounds') {
                        setSelectedRound(event.rounds[0] || null);
                      } else {
                        setSelectedRound(null);
                      }
                      setRoundsCollapsed(false);
                    }}
                  >
                    <div className="event-card-header">
                      <div className="event-name">{event.name}</div>
                      <button 
                        className="event-settings-btn"
                        onClick={(e) => { e.stopPropagation(); openEditEventLogo(event); }}
                        title="Logo instellen"
                      >
                        {event.logoUrl ? '🖼️' : '⚙️'}
                      </button>
                    </div>
                    <div className="event-date">{formatDate(event.date)}</div>
                    <div className="event-stats">
                      {getEventType(event) === 'theme-night'
                        ? `${getEventSongCount(event)} nummers • Thema-avond`
                        : `${event.rounds.length} rondes • ${getEventSongCount(event)} nummers`}
                    </div>
                  </div>
                ))}
                {events.length === 0 && (
                  <div className="empty-state">
                    <p>Geen evenementen</p>
                    <p className="hint">Maak je eerste evenement aan!</p>
                  </div>
                )}
              </div>
              )}
            </>
          )}
          {/* Rounds for selected event */}
          {selectedEvent && (
            <div className="rounds-section">
              <button 
                className="back-to-events-btn"
                onClick={() => { setSelectedEvent(null); setSelectedRound(null); }}
              >
                ← Terug naar evenementen
              </button>
              
              <div className="selected-event-header">
                <h2>📅 {selectedEvent.name}</h2>
                <button 
                  className="event-settings-btn"
                  onClick={() => openEditEventLogo(selectedEvent)}
                  title="Logo instellen"
                >
                  {selectedEvent.logoUrl ? '🖼️' : '⚙️'}
                </button>
              </div>

              <div 
                className="panel-header collapsible"
                onClick={() => setRoundsCollapsed(!roundsCollapsed)}
              >
                <h3>
                  <span className={`collapse-icon ${roundsCollapsed ? 'collapsed' : ''}`}>▼</span>
                  {getEventType(selectedEvent) === 'theme-night' 
                    ? `🎵 Thema-lijst ${(selectedEvent.themeSongs || []).length > 0 ? `(${(selectedEvent.themeSongs || []).length})` : ''}`
                    : '🎯 Rondes'}
                </h3>
                <div className="panel-header-buttons">
                  {getEventType(selectedEvent) === 'rounds' ? (
                    <button className="add-btn" onClick={(e) => { e.stopPropagation(); setShowRoundModal(true); }}>
                      + Ronde
                    </button>
                  ) : (
                    <>
                      {(selectedEvent.themeSongs || []).length > 0 && (
                        <button 
                          className="overview-btn"
                          onClick={(e) => { e.stopPropagation(); setShowSongOverview(true); }}
                          title="Bekijk alle nummers in groot formaat"
                        >
                          📋 Overzicht
                        </button>
                      )}
                      <button className="wizard-btn" onClick={(e) => { e.stopPropagation(); setShowPlaylistWizard(true); }}>
                        ✨ Playlist Wizard
                      </button>
                    </>
                  )}
                </div>
              </div>
              {!roundsCollapsed && (
              <div className="rounds-list">
                {getEventType(selectedEvent) === 'rounds' ? (
                  selectedEvent.rounds.map(round => (
                    <div 
                      key={round.id}
                      className={`round-card ${selectedRound?.id === round.id ? 'selected' : ''}`}
                      onClick={() => setSelectedRound(round)}
                    >
                      <div className="round-info">
                        <span className="round-name">{round.name}</span>
                        <span className="round-variant-chip">{getRoundVariantLabel(round.hitsterVariant)}</span>
                        <span className="round-count">{round.songs.length} nummers</span>
                      </div>
                      <div className="round-card-actions" onClick={e => e.stopPropagation()}>
                        <button
                          className="round-action-btn"
                          title="Exporteer als JSON"
                          onClick={() => {
                            const payload = {
                              event: { id: selectedEvent.id, name: selectedEvent.name, date: selectedEvent.date },
                              round: {
                                id: round.id,
                                name: round.name,
                                order: round.order,
                                songs: round.songs
                                  .slice()
                                  .sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
                              },
                              exportedAt: new Date().toISOString(),
                            };
                            const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${selectedEvent.name} - ${round.name}.json`.replace(/[/\\?%*:|"<>]/g, '-');
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                            URL.revokeObjectURL(url);
                          }}
                        >
                          ⬇ JSON
                        </button>
                        <button
                          className="round-action-btn"
                          title="Print / PDF"
                          onClick={() => navigate(`/print-round/${selectedEvent.id}/${round.id}`)}
                        >
                          🖨️
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => deleteRound(round.id)}
                        >
                          ✕
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  (() => {
                    const themeSongs = [...(selectedEvent.themeSongs || [])]
                      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

                    if (themeSongs.length === 0) {
                      return (
                        <div className="empty-state">
                          <p>Nog geen nummers in thema-lijst.</p>
                          <p className="hint">Gebruik de Playlist Wizard om snel een lijst te genereren.</p>
                        </div>
                      );
                    }

                    return (
                      <>
                        {themeNightSetHistory.length > 0 && (
                          <div className="theme-night-sets-list">
                            {themeNightSetHistory.map((setItem) => {
                              const setNumber = getThemeNightSetNumberFromId(setItem.setId);
                              const setLabel = setNumber ? `Set ${setNumber}` : setItem.setId;
                              return (
                                <div
                                  key={setItem.setId}
                                  className={`round-card ${selectedThemeNightSetId === setItem.setId ? 'selected' : ''}`}
                                  onClick={() => setSelectedThemeNightSetId(setItem.setId)}
                                  title="Selecteer set"
                                >
                                  <div className="round-info">
                                    <span className="round-name">🎯 {setLabel}</span>
                                    <span className="round-count">{setItem.count}/10 nummers</span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        )}
                        <div className="right-songs-list">
                          {themeSongs.map((song, index) => (
                            <div key={song.id} className="right-song-item">
                              <button
                                type="button"
                                className="song-number-button"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setShowSongOverview(true);
                                }}
                                title="Open overzicht"
                              >
                                {index + 1}
                              </button>
                              {song.imageUrl && <img src={song.imageUrl} alt="" className="song-thumb" />}
                              <div className="song-info">
                                <div className="song-title">{song.title}</div>
                                <div className="song-artist">{song.artist} • {song.year}</div>
                                <div className="song-set-chip">{getSongSetLabel(song.id)}</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()
                )}
              </div>
              )}
            </div>
          )}
          {/* Link to Events management */}
          {selectedEvent && !selectedRound && (
            <div className="events-link-section">
              <Button variant="secondary" onClick={() => navigate('/events')}>
                📅 Beheer evenementen, teams & rondes
              </Button>
            </div>
          )}
          {/* Songs in selected round / theme-night controls */}
          {(selectedRound || isThemeNightSelected) && (
            <div className="round-songs-section">
              {/* Back button to return to rounds list */}
              <button 
                className="back-to-rounds-btn"
                onClick={() => {
                  if (selectedRound) {
                    setSelectedRound(null);
                  } else {
                    setSelectedEvent(null);
                  }
                }}
              >
                {selectedRound ? '← Terug naar rondes' : '← Terug naar evenementen'}
              </button>
              
              {/* Selected round header */}
              <div className="selected-round-header">
                <div className="selected-round-header-main">
                  <h3>🎵 {selectedRound?.name || 'Thema-avond'} ({selectedSongList.length})</h3>
                  {selectedRound && (
                    <div className="selected-round-variant-row">
                      <label htmlFor="selected-round-variant">Spelvariant</label>
                      <select
                        id="selected-round-variant"
                        value={selectedRound.hitsterVariant || 'classic'}
                        onChange={(event) => updateRoundVariant(event.target.value as HitsterRoundVariant)}
                      >
                        {HITSTER_ROUND_VARIANTS.map((variant) => (
                          <option key={variant.id} value={variant.id}>{variant.label}</option>
                        ))}
                      </select>
                    </div>
                  )}
                </div>
                {selectedSongList.length > 0 && (
                  <button 
                    className="overview-btn"
                    onClick={() => setShowSongOverview(true)}
                    title="Bekijk alle nummers in groot formaat"
                  >
                    📋 Overzicht
                  </button>
                )}
              </div>

              {selectedRound && (selectedRound.hitsterVariant || 'classic') === 'battle-generations' && (
                <div className="generation-period-panel">
                  <div className="generation-period-panel-header">
                    <strong>Battle of the Generations</strong>
                    <span>Kies per beurt de periode en start daarna pas het nummer.</span>
                  </div>
                  <div className="generation-period-buttons">
                    {HITSTER_GENERATION_PERIODS.map((period) => (
                      <button
                        key={period.id}
                        type="button"
                        className={`generation-period-btn ${selectedGenerationPeriodId === period.id ? 'active' : ''}`}
                        onClick={() => setSelectedGenerationPeriodId(period.id)}
                      >
                        <span className="generation-period-label">
                          {period.iconUrl && (
                            <img src={period.iconUrl} alt="" className="generation-period-icon" />
                          )}
                          {period.label}
                        </span>
                        <span className="generation-period-years">{period.yearLabel}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Action Buttons */}
              <div className="qr-scanner-btn-container">
                <button className="qr-scanner-btn" onClick={startQrScanner}>
                  📷 QR Code Scannen
                </button>
                <button className="wizard-btn" onClick={() => setShowPlaylistWizard(true)}>
                  ✨ Playlist Wizard
                </button>
                {isPlayingAllRounds ? (
                  <button className="play-all-btn stop" onClick={stopPlayAllRounds}>
                    ⏹ Stop alles
                  </button>
                ) : (
                  <button
                    className="play-all-btn"
                    onClick={playAllRounds}
                    disabled={!selectedEvent || selectedEvent.rounds.every(round => round.songs.length === 0)}
                  >
                    ▶️ Speel alle rondes
                  </button>
                )}
              </div>
              {playAllStatus && <div className="play-all-status">{playAllStatus}</div>}
              {isThemeNightSelected && (
                <div className="play-all-status">
                  {themeNightSetSaving
                    ? 'Thema-set wordt opgeslagen...'
                    : (themeNightSetStatus || `Thema-set: ${themeNightSetSongIds.length}/10`)}
                </div>
              )}
              {isThemeNightSelected && themeNightSetHistory.length > 0 && (
                <div className="play-all-status">
                  <strong>Set geschiedenis</strong>
                  <div className="theme-night-history-buttons">
                    {themeNightSetHistory.map((setItem) => (
                      <div key={setItem.setId} className="theme-night-history-item">
                        <span className="theme-night-history-label">
                          {(() => {
                            const setNumber = getThemeNightSetNumberFromId(setItem.setId);
                            const setLabel = setNumber ? `Set ${setNumber}` : setItem.setId;
                            return `${setLabel} (${setItem.count}/10)`;
                          })()}
                        </span>
                        <button
                          type="button"
                          className="round-action-btn set-open"
                          onClick={() => {
                            setSelectedThemeNightSetId(setItem.setId);
                            openThemeNightSetAnswers(setItem.setId, 'answers');
                          }}
                          title="Open antwoordsleutel voor deze set"
                        >
                          📋 Antwoorden
                        </button>
                        <button
                          type="button"
                          className="round-action-btn set-open"
                          onClick={() => {
                            setSelectedThemeNightSetId(setItem.setId);
                            openThemeNightSetAnswers(setItem.setId, 'teams');
                          }}
                          title="Open team score/invoer voor deze set"
                        >
                          👥 Teams
                        </button>
                      </div>
                    ))}
                  </div>
                  {selectedThemeNightSetId && (
                    <div className="theme-night-history-item" style={{ marginTop: 10 }}>
                      <span className="theme-night-history-label">
                        Geselecteerd: {(() => {
                          const setNumber = getThemeNightSetNumberFromId(selectedThemeNightSetId);
                          return setNumber ? `Set ${setNumber}` : selectedThemeNightSetId;
                        })()}
                      </span>
                      <button
                        type="button"
                        className="round-action-btn set-open"
                        onClick={() => openThemeNightSetAnswers(selectedThemeNightSetId, 'answers')}
                      >
                        📋 Antwoorden openen
                      </button>
                      <button
                        type="button"
                        className="round-action-btn set-open"
                        onClick={() => openThemeNightSetAnswers(selectedThemeNightSetId, 'teams')}
                      >
                        👥 Teams openen
                      </button>
                    </div>
                  )}
                </div>
              )}
              {isThemeNightSelected && (
                <div className="play-all-status">
                  <strong>Huidige set ({themeNightCurrentSetSongs.length}/10)</strong>
                  {themeNightCurrentSetSongs.length === 0 ? (
                    <div>Nog geen nummers in de huidige set.</div>
                  ) : (
                    <div>
                      {themeNightCurrentSetSongs.map((song, index) => (
                        <div key={song.id}>{index + 1}. {song.artist} - {song.title}</div>
                      ))}
                    </div>
                  )}
                  {selectedEvent && (
                    <button
                      className="round-action-btn"
                      onClick={() => navigate(`/music-bingo/${selectedEvent.id}?mode=hitster`)}
                      title="Open antwoordsleutels in Hitster Bingo"
                    >
                      🔑 Open antwoordsleutels
                    </button>
                  )}
                </div>
              )}
              
              {/* Live Display Controls */}
              {(liveSongId || isIntermissionActive) && (
                <div className="live-display-controls">
                  <span className="live-indicator">{isIntermissionActive ? '⏸️ PAUZE' : '🔴 LIVE'}</span>
                  {!isIntermissionActive && (
                    <div className="fade-duration-control">
                      <label htmlFor="fade-duration-live">Fade</label>
                      <input
                        id="fade-duration-live"
                        type="range"
                        min={1}
                        max={10}
                        step={0.5}
                        value={fadeDurationSeconds}
                        onChange={(e) => handleFadeDurationChange(parseFloat(e.target.value))}
                      />
                      <span>{fadeDurationLabel}</span>
                    </div>
                  )}
                  <button
                    className={`intermission-btn ${isIntermissionActive ? 'active' : ''}`}
                    onClick={toggleIntermissionScreen}
                  >
                    {isIntermissionActive ? '▶️ Hervat scherm' : '🍻 Toon pauzescherm'}
                  </button>
                  <button
                    className={`intermission-btn ${isScoreboardActive ? 'active' : ''}`}
                    onClick={isScoreboardActive ? hideScoreboardOnDisplay : showScoreboardOnDisplay}
                  >
                    {isScoreboardActive ? '❌ Verberg scorebord' : '🏆 Toon scorebord'}
                  </button>
                  <button
                    className={`intermission-btn ${isRoundAnswersActive ? 'active' : ''}`}
                    onClick={isRoundAnswersActive ? hideRoundAnswersOnDisplay : showRoundAnswersOnDisplay}
                    disabled={selectedSongList.length === 0}
                  >
                    {isRoundAnswersActive ? '❌ Verberg antwoorden' : '🎵 Toon ronde-antwoorden'}
                  </button>
                  {!isIntermissionActive && (
                    <>
                      <button 
                        className={`pause-btn ${isYoutubePaused ? 'paused' : ''}`}
                        onClick={toggleYoutubePause}
                      >
                        {isYoutubePaused ? '▶️ Play' : '⏸️ Pauze'}
                      </button>
                      <button className="reveal-btn" onClick={revealOnDisplay}>✅ Onthul</button>
                      <button 
                        className="fade-btn" 
                        onClick={triggerFadeOut} 
                        disabled={isFading}
                      >
                        {isFading ? '🔉 Fading...' : '🔊➡️🔇 Fade'}
                      </button>
                    </>
                  )}
                  <button className="stop-btn" onClick={stopDisplay}>⏹ Stop</button>
                </div>
              )}

              {!liveSongId && !isIntermissionActive && (
                <div className="display-secondary-controls">
                  <button className="intermission-btn" onClick={toggleIntermissionScreen}>
                    🍻 Toon pauzescherm
                  </button>
                  <button
                    className={`intermission-btn ${isScoreboardActive ? 'active' : ''}`}
                    onClick={isScoreboardActive ? hideScoreboardOnDisplay : showScoreboardOnDisplay}
                  >
                    {isScoreboardActive ? '❌ Verberg scorebord' : '🏆 Toon scorebord'}
                  </button>
                  <button
                    className={`intermission-btn ${isRoundAnswersActive ? 'active' : ''}`}
                    onClick={isRoundAnswersActive ? hideRoundAnswersOnDisplay : showRoundAnswersOnDisplay}
                    disabled={selectedSongList.length === 0}
                  >
                    {isRoundAnswersActive ? '❌ Verberg antwoorden' : '🎵 Toon ronde-antwoorden'}
                  </button>
                </div>
              )}
              
              {/* Tab navigation voor Discogs / Tools - Nummers staat nu rechts */}
              {selectedRound && selectedRound.songs.length > 0 && (
              <div className="round-tabs">
                <button 
                  className={`round-tab ${roundViewTab === 'discogs' ? 'active' : ''}`}
                  onClick={() => setRoundViewTab('discogs')}
                >
                  💿 Discogs
                </button>
                <button 
                  className={`round-tab ${roundViewTab === 'tools' ? 'active' : ''}`}
                  onClick={() => setRoundViewTab('tools')}
                >
                  🛠️ Tools
                </button>
              </div>
              )}
              
              {/* Discogs Verificatie Tab */}
              {roundViewTab === 'discogs' && selectedRound && selectedRound.songs.length > 0 && (
                <DiscogsVerify 
                  songs={selectedRound.songs} 
                  onUpdateSong={updateSongFromDiscogs}
                />
              )}
              
              {/* Tools Tab (Beta) */}
              {roundViewTab === 'tools' && selectedRound && selectedRound.songs.length > 0 && (
                <SongTools 
                  songs={selectedRound.songs} 
                  onUpdateSong={updateSongFromDiscogs}
                  onRemoveSong={removeSongFromRound}
                />
              )}
            </div>
          )}
        </div>
        {/* Right Panel: Search / Songs */}
        <div className="panel search-panel">
          {/* Tab navigation when a round is selected */}
          {selectedRound && (
            <div className="right-panel-tabs">
              <button 
                className={`right-panel-tab ${rightPanelTab === 'nummers' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('nummers')}
              >
                🎵 Nummers ({selectedRound.songs.length})
              </button>
              <button 
                className={`right-panel-tab ${rightPanelTab === 'zoeken' ? 'active' : ''}`}
                onClick={() => setRightPanelTab('zoeken')}
              >
                🔍 Zoeken
              </button>
            </div>
          )}
          
          {/* Songs list when nummers tab is active */}
          {selectedRound && rightPanelTab === 'nummers' && (
            <div className="right-panel-songs">
              {isBattleGenerationsRound && selectedRound.songs.length > 0 && (
                <div className="overview-generation-filters right-panel-generation-filters">
                  {GENERATION_FILTER_OPTIONS.map((filter) => (
                    <button
                      key={filter.id}
                      type="button"
                      className={`overview-generation-filter-btn ${rightPanelGenerationFilter === filter.id ? 'active' : ''}`}
                      onClick={() => setRightPanelGenerationFilter(filter.id)}
                    >
                      {filter.label}
                    </button>
                  ))}
                  <span className="overview-generation-filter-count">{rightPanelSongs.length} / {selectedRound.songs.length}</span>
                </div>
              )}
              {selectedRound.songs.length === 0 ? (
                <div className="empty-state">
                  <p>Nog geen nummers in deze ronde.</p>
                  <button className="add-songs-btn" onClick={() => setRightPanelTab('zoeken')}>
                    🔍 Zoek nummers om toe te voegen
                  </button>
                </div>
              ) : rightPanelSongs.length === 0 ? (
                <div className="empty-state">
                  <p>Geen nummers in deze filter.</p>
                  <button className="add-songs-btn" onClick={() => setRightPanelGenerationFilter('all')}>
                    Toon alles
                  </button>
                </div>
              ) : (
                <div className="right-songs-list">
                  {rightPanelSongs.map((song, index) => (
                    <div key={song.id} className="right-song-item-wrapper">
                      <div
                        className={`right-song-item ${playingSongId === song.id || playingYouTubeId === song.id ? 'playing' : ''} ${liveSongId === song.id ? 'live' : ''} ${armedDisplaySongId === song.id ? 'armed' : ''} ${draggedSongId === song.id ? 'dragging' : ''} ${dropTargetSongId === song.id ? 'drop-target' : ''}`}
                        draggable
                        onDragStart={(event) => handleSongDragStart(song.id, event)}
                        onDragOver={(event) => handleSongDragOver(song.id, event)}
                        onDrop={(event) => void handleSongDrop(song.id, event)}
                        onDragEnd={handleSongDragEnd}
                      >
                        <button
                          type="button"
                          className={`song-number-button ${armedDisplaySongId === song.id ? 'armed' : ''}`}
                          onClick={(event) => {
                            event.preventDefault();
                            event.stopPropagation();
                            void armSongForDisplay(song, selectedRound, selectedRound.order - 1);
                          }}
                          title="Zet dit nummer klaar als volgende scan op het display"
                        >
                          {index + 1}
                        </button>
                        {song.imageUrl && <img src={song.imageUrl} alt="" className="song-thumb" />}
                        <div className="song-info">
                          <div className="song-title">{song.title}</div>
                          <div className="song-artist">{song.artist} • {song.year}</div>
                          {(selectedRound.hitsterVariant || 'classic') === 'battle-generations' && getGenerationPeriodForYear(song.year) && (
                            <div className="song-generation-chip">
                              {getGenerationPeriodForYear(song.year)?.label} • {getGenerationPeriodForYear(song.year)?.yearLabel}
                            </div>
                          )}
                        </div>
                        <div className="song-play-actions">
                          {/* Quick volume adjust buttons */}
                          <button
                            className="song-volume-btn minus"
                            onClick={() => adjustSongVolume(song.id, (song.volumeGain ?? 1.0) * 100 - 10)}
                            title="Volume lager"
                          >
                            🔉
                          </button>
                          <span className="song-volume-indicator" title="Volume ingestelling">
                            {song.volumeGain ? `${Math.round(song.volumeGain * 100)}%` : '100%'}
                          </span>
                          <button
                            className="song-volume-btn plus"
                            onClick={() => adjustSongVolume(song.id, (song.volumeGain ?? 1.0) * 100 + 10)}
                            title="Volume hoger"
                          >
                            🔊
                          </button>
                          {/* Spotify play button */}
                          {song.spotifyUri && isAuthenticated && isPremium && isReady && (
                            <SpotifyPlayButton 
                              trackUri={song.spotifyUri}
                              trackName={song.title}
                            />
                          )}
                          {/* YouTube embed play */}
                          {song.youtubeVideoId && (
                            <button 
                              className={`song-play-btn youtube ${playingYouTubeId === song.id ? 'playing' : ''}`}
                              onClick={() => playYouTubeSong(song)}
                              title="YouTube afspelen"
                            >
                              {playingYouTubeId === song.id ? '⏹' : '📺'}
                            </button>
                          )}
                          {/* Song settings button */}
                          <button 
                            className={`song-play-btn settings ${song.volumeGain !== undefined || song.youtubeStartTime || song.youtubeEndTime ? 'has-settings' : ''}`}
                            onClick={() => openYoutubeTimingEditor(song)}
                            title="Instellingen (volume, timing, etc)"
                          >
                            ⚙️
                          </button>
                          {/* Audio play */}
                          {(song.audioUrl || song.previewUrl) && !song.youtubeVideoId && (
                            <button 
                              className={`song-play-btn audio ${playingSongId === song.id ? 'playing' : ''}`}
                              onClick={() => playSongAudio(song)}
                              title="Audio afspelen"
                            >
                              {playingSongId === song.id ? '⏹' : '▶'}
                            </button>
                          )}
                        </div>
                        {/* Display controls */}
                        {liveSongId === song.id ? (
                          <div className="display-song-controls">
                            <span className="live-indicator-small">🔴</span>
                            <button 
                              className={`song-pause-btn ${isYoutubePaused ? 'paused' : ''}`}
                              onClick={toggleYoutubePause}
                              title={isYoutubePaused ? 'Hervatten' : 'Pauzeren'}
                            >
                              {isYoutubePaused ? '▶️' : '⏸️'}
                            </button>
                          </div>
                        ) : (
                          <button 
                            className="live-display-btn"
                            onPointerDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              void playOnDisplay(song, selectedRound, selectedRound.order - 1);
                            }}
                            title="Toon op Display scherm"
                          >
                            🖥️
                          </button>
                        )}
                        <button 
                          className={`lyrics-btn ${song.lyrics ? 'has-lyrics' : ''}`}
                          onClick={() => openLyricsEditor(song)}
                          title={song.lyrics ? 'Songtekst bewerken' : 'Songtekst toevoegen'}
                        >
                          📝
                        </button>
                        {selectedEvent && selectedEvent.rounds.length > 1 && (
                          <div className="move-to-round-wrapper">
                            <button
                              className={`move-round-btn ${movingSongId === song.id ? 'active' : ''}`}
                              onClick={() => setMovingSongId(movingSongId === song.id ? null : song.id)}
                              title="Verplaatsen naar andere ronde"
                            >
                              ↕️
                            </button>
                            {movingSongId === song.id && (
                              <div className="move-round-dropdown">
                                <div className="move-round-label">Verplaats naar:</div>
                                {selectedEvent.rounds
                                  .filter(r => r.id !== selectedRound?.id)
                                  .sort((a, b) => a.order - b.order)
                                  .map(r => (
                                    <button
                                      key={r.id}
                                      className="move-round-option"
                                      onClick={() => moveSongToRound(song.id, r.id)}
                                    >
                                      {r.name}
                                    </button>
                                  ))}
                              </div>
                            )}
                          </div>
                        )}
                        <button 
                          className="remove-btn"
                          onClick={() => removeSongFromRound(song.id)}
                          title="Verwijderen"
                        >
                          ✕
                        </button>
                      </div>
                      {/* YouTube embed player */}
                      {playingYouTubeId === song.id && song.youtubeVideoId && (
                        <div className="song-youtube-player">
                          <iframe
                            src={`https://www.youtube.com/embed/${song.youtubeVideoId}?autoplay=1`}
                            title={song.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
          
          {/* Theme-night songs display */}
          {isThemeNightSelected && selectedEvent && !selectedRound && themeDisplayRound && (
            <div className="round-songs-section">
              {/* Back button to return to events list */}
              <button 
                className="back-to-rounds-btn"
                onClick={() => setSelectedEvent(null)}
              >
                ← Terug naar evenementen
              </button>
              
              {/* Theme-night header */}
              <div className="selected-round-header">
                <div className="selected-round-header-main">
                  <h3>🎵 Thema-avond ({selectedEvent.themeSongs?.length || 0})</h3>
                </div>
                {(selectedEvent.themeSongs || []).length > 0 && (
                  <button 
                    className="overview-btn"
                    onClick={() => setShowSongOverview(true)}
                    title="Bekijk alle nummers in groot formaat"
                  >
                    📋 Overzicht
                  </button>
                )}
              </div>
              
              {/* Theme songs list */}
              {(selectedEvent.themeSongs || []).length === 0 ? (
                <div className="empty-state">
                  <p>Nog geen nummers in thema-lijst.</p>
                  <p className="hint">Gebruik de Playlist Wizard om snel een lijst te genereren.</p>
                </div>
              ) : (
                <div className="songs-container">
                  {[...(selectedEvent.themeSongs || [])]
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map((song, index) => (
                      <div key={song.id} className="right-song-item-wrapper">
                        <div
                          className={`right-song-item ${playingSongId === song.id || playingYouTubeId === song.id ? 'playing' : ''} ${liveSongId === song.id ? 'live' : ''} ${draggedSongId === song.id ? 'dragging' : ''} ${dropTargetSongId === song.id ? 'drop-target' : ''}`}
                          draggable
                          onDragStart={(event) => handleSongDragStart(song.id, event)}
                          onDragOver={(event) => handleSongDragOver(song.id, event)}
                          onDrop={(event) => void handleSongDrop(song.id, event)}
                          onDragEnd={handleSongDragEnd}
                        >
                          <button
                            type="button"
                            className={`song-number-button ${armedDisplaySongId === song.id ? 'armed' : ''}`}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              void armSongForDisplay(song, themeDisplayRound, themeDisplayRound.order - 1);
                            }}
                            title={`Zet klaar voor display (Set ${activeThemeNightSetNumber}, plek ${activeThemeNightSetSlot})`}
                          >
                            {index + 1}
                          </button>
                          {song.imageUrl && <img src={song.imageUrl} alt="" className="song-thumb" />}
                          <div className="song-info">
                            <div className="song-title">{song.title}</div>
                            <div className="song-artist">{song.artist} • {song.year}</div>
                            <div className="song-set-chip">{getSongSetLabel(song.id)}</div>
                          </div>
                          <div className="song-play-actions">
                            <button
                              className="song-volume-btn minus"
                              onClick={() => adjustSongVolume(song.id, (song.volumeGain ?? 1.0) * 100 - 10)}
                              title="Volume lager"
                            >
                              🔉
                            </button>
                            <span className="song-volume-indicator" title="Volume ingestelling">
                              {song.volumeGain ? `${Math.round(song.volumeGain * 100)}%` : '100%'}
                            </span>
                            <button
                              className="song-volume-btn plus"
                              onClick={() => adjustSongVolume(song.id, (song.volumeGain ?? 1.0) * 100 + 10)}
                              title="Volume hoger"
                            >
                              🔊
                            </button>
                            {song.spotifyUri && isAuthenticated && isPremium && isReady && (
                              <SpotifyPlayButton
                                trackUri={song.spotifyUri}
                                trackName={song.title}
                              />
                            )}
                            {song.youtubeVideoId && (
                              <button
                                className={`song-play-btn youtube ${playingYouTubeId === song.id ? 'playing' : ''}`}
                                onClick={() => playYouTubeSong(song)}
                                title="YouTube afspelen"
                              >
                                {playingYouTubeId === song.id ? '⏹' : '📺'}
                              </button>
                            )}
                            <button
                              className={`song-play-btn settings ${song.volumeGain !== undefined || song.youtubeStartTime || song.youtubeEndTime ? 'has-settings' : ''}`}
                              onClick={() => openYoutubeTimingEditor(song)}
                              title="Instellingen (volume, timing, etc)"
                            >
                              ⚙️
                            </button>
                            <button
                              className="song-play-btn set-manual"
                              onClick={() => void addSongToManualThemeNightSet(song)}
                              title="Handmatig toevoegen aan set 1,2,3..."
                            >
                              ➕
                            </button>
                            {(song.audioUrl || song.previewUrl) && !song.youtubeVideoId && (
                              <button
                                className={`song-play-btn audio ${playingSongId === song.id ? 'playing' : ''}`}
                                onClick={() => playSongAudio(song)}
                                title="Audio afspelen"
                              >
                                {playingSongId === song.id ? '⏹' : '▶'}
                              </button>
                            )}
                          </div>
                          {liveSongId === song.id ? (
                            <div className="display-song-controls">
                              <span className="live-indicator-small">🔴</span>
                              <button
                                className={`song-pause-btn ${isYoutubePaused ? 'paused' : ''}`}
                                onClick={toggleYoutubePause}
                                title={isYoutubePaused ? 'Hervatten' : 'Pauzeren'}
                              >
                                {isYoutubePaused ? '▶️' : '⏸️'}
                              </button>
                            </div>
                          ) : (
                            <button
                              className="live-display-btn"
                              onPointerDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                void playOnDisplay(song, themeDisplayRound, themeDisplayRound.order - 1);
                              }}
                              title={`Toon op Display (gaat naar Set ${activeThemeNightSetNumber}, plek ${activeThemeNightSetSlot})`}
                            >
                              🖥️
                            </button>
                          )}
                          <div className="song-play-actions">
                            <button 
                              className={`lyrics-btn ${song.lyrics ? 'has-lyrics' : ''}`}
                              onClick={() => openLyricsEditor(song)}
                              title={song.lyrics ? 'Songtekst bewerken' : 'Songtekst toevoegen'}
                            >
                              📝
                            </button>
                            <button 
                              className="remove-btn"
                              onClick={() => removeSongFromRound(song.id)}
                              title="Verwijderen"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                        {playingYouTubeId === song.id && song.youtubeVideoId && (
                          <div className="song-youtube-player">
                            <iframe
                              src={`https://www.youtube.com/embed/${song.youtubeVideoId}?autoplay=1`}
                              title={song.title}
                              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                              allowFullScreen
                            />
                          </div>
                        )}
                      </div>
                    ))}
                </div>
              )}
            </div>
          )}
          
          {/* Search content - show when no round selected OR zoeken tab is active */}
          {((!selectedRound && !isThemeNightSelected) || rightPanelTab === 'zoeken') && (
            <>
          {!selectedRound && !isThemeNightSelected && (
          <div className="panel-header">
            <h2>🔍 Zoeken</h2>
          </div>
          )}
          {/* Search mode toggle */}
          <div className="search-mode-toggle">
            <button 
              className={`mode-btn ${searchMode === 'spotify' ? 'active' : ''}`}
              onClick={() => setSearchMode('spotify')}
            >
              🎧 Spotify
            </button>
            <button 
              className={`mode-btn ${searchMode === 'youtube' ? 'active' : ''}`}
              onClick={() => setSearchMode('youtube')}
            >
              📺 YouTube
            </button>
            <button 
              className={`mode-btn ${searchMode === 'downloads' ? 'active' : ''}`}
              onClick={() => setSearchMode('downloads')}
            >
              📁 Downloads
            </button>
          </div>
          {searchMode === 'spotify' && (
            <>
          {/* Decade filters */}
          <div className="decade-filters">
            {DECADES.map(decade => (
              <button
                key={decade.label}
                className={`decade-btn ${selectedDecade === decade.label ? 'active' : ''}`}
                onClick={() => handleDecadeClick(decade)}
              >
                {decade.label}
              </button>
            ))}
          </div>
          {/* Search form */}
          <div className="search-form">
            <div className="search-row">
              <div className="search-field">
                <label>Artiest</label>
                <input
                  type="text"
                  value={artistFilter}
                  onChange={(e) => setArtistFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="bijv. Queen"
                />
              </div>
              <div className="search-field">
                <label>Nummer</label>
                <input
                  type="text"
                  value={trackFilter}
                  onChange={(e) => setTrackFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="bijv. Bohemian Rhapsody"
                />
              </div>
            </div>
            <div className="search-row">
              <div className="search-field">
                <label>Album</label>
                <input
                  type="text"
                  value={albumFilter}
                  onChange={(e) => setAlbumFilter(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="bijv. A Night at the Opera"
                />
              </div>
              <div className="search-field year-range">
                <label>Jaar</label>
                <div className="year-inputs">
                  <input
                    type="number"
                    value={yearFrom}
                    onChange={(e) => setYearFrom(e.target.value)}
                    placeholder="Van"
                    min="1900"
                    max="2030"
                  />
                  <span>-</span>
                  <input
                    type="number"
                    value={yearTo}
                    onChange={(e) => setYearTo(e.target.value)}
                    placeholder="Tot"
                    min="1900"
                    max="2030"
                  />
                </div>
              </div>
            </div>
            <div className="search-row">
              <div className="search-field full">
                <label>Vrij zoeken</label>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Zoek op alles..."
                />
              </div>
            </div>
            <div className="search-options">
              <label className="checkbox-option">
                <input
                  type="checkbox"
                  checked={onlyWithPreview}
                  onChange={(e) => setOnlyWithPreview(e.target.checked)}
                />
                Alleen met preview
              </label>
              <div className="limit-option">
                <label>Max:</label>
                <select value={searchLimit} onChange={(e) => setSearchLimit(Number(e.target.value))}>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="genre-option">
                <label>Genre:</label>
                <select value={genreFilter} onChange={(e) => setGenreFilter(e.target.value)}>
                  <option value="">Alle genres</option>
                  <option value="pop">Pop</option>
                  <option value="rock">Rock</option>
                  <option value="hip-hop">Hip-Hop</option>
                  <option value="r-n-b">R&B</option>
                  <option value="dance">Dance</option>
                  <option value="electronic">Electronic</option>
                  <option value="house">House</option>
                  <option value="disco">Disco</option>
                  <option value="soul">Soul</option>
                  <option value="jazz">Jazz</option>
                  <option value="classical">Classical</option>
                  <option value="reggae">Reggae</option>
                  <option value="country">Country</option>
                  <option value="metal">Metal</option>
                  <option value="punk">Punk</option>
                  <option value="latin">Latin</option>
                  <option value="dutch">Nederlands</option>
                </select>
              </div>
            </div>
            <div className="search-actions">
              <Button variant="secondary" onClick={clearFilters}>
                Wissen
              </Button>
              <Button variant="primary" onClick={handleSearch} disabled={loading}>
                {loading ? 'Zoeken...' : '🔍 Zoeken'}
              </Button>
            </div>
          </div>
          {error && <p className="error">{error}</p>}
          {searchResults.length > 0 && (
            <div className="search-results-summary">
              <div>
                <div className="search-results-count">{searchResults.length} nummers gevonden</div>
                <div className="search-results-hint">Open de popup om meerdere resultaten tegelijk te bekijken en toe te voegen.</div>
              </div>
              <Button variant="primary" onClick={() => setShowSpotifyResultsModal(true)}>
                Bekijk resultaten
              </Button>
            </div>
          )}
          </>
          )}
          {searchMode === 'youtube' && (
            <div className="youtube-search-section">
              <div className="yt-search-box">
                <input
                  type="text"
                  value={ytSearchQuery}
                  onChange={(e) => setYtSearchQuery(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleYouTubeSearch()}
                  placeholder="Zoek op artiest, titel of plak YouTube/Dailymotion URL..."
                  disabled={ytIsSearching || ytIsDownloading}
                />
                <Button
                  variant="primary"
                  onClick={handleYouTubeSearch}
                  disabled={ytIsSearching || !ytSearchQuery.trim()}
                >
                  {ytIsSearching ? '⏳ Zoeken...' : '🔍 Zoeken'}
                </Button>
              </div>
              {error && <p className="error">{error}</p>}
              {ytDownloadStatus && <div className="yt-status-message">{ytDownloadStatus}</div>}
              {/* YouTube Search Results */}
              {ytResults.length > 0 && !ytSelectedVideo && (
                <div className="yt-search-results">
                  {ytResults.map((video) => (
                    <div key={video.id} className="yt-result-wrapper">
                      {ytPreviewingId === video.id ? (
                        <div className="yt-preview-player">
                          <div className="yt-preview-header">
                            <span className="yt-preview-title">{video.title}</span>
                            <button 
                              className="yt-preview-close"
                              onClick={() => setYtPreviewingId(null)}
                            >
                              ✕ Sluiten
                            </button>
                          </div>
                          <iframe
                            src={`https://www.youtube.com/embed/${video.id}?autoplay=1`}
                            title={video.title}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            className="yt-preview-iframe"
                          />
                          <div className="yt-preview-actions">
                            <Button
                              variant="success"
                              onClick={() => {
                                setYtPreviewingId(null);
                                handleYouTubeSelectVideo(video);
                              }}
                            >
                              ✔ Selecteer voor download
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div
                          className="yt-result-item"
                        >
                          <img src={video.thumbnail} alt={video.title} className="yt-result-thumb" />
                          <div className="yt-result-info">
                            <div className="yt-result-title">{video.title}</div>
                            <div className="yt-result-channel">
                              {video.channel} • {formatDuration(video.duration)}
                            </div>
                          </div>
                          <div className="yt-result-actions">
                            <button 
                              className="yt-play-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setYtPreviewingId(video.id);
                              }}
                              title="Preview afspelen"
                            >
                              ▶
                            </button>
                            <button 
                              className="yt-select-btn"
                              onClick={() => handleYouTubeSelectVideo(video)}
                              title="Selecteren"
                            >
                              ↓
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Loading video info */}
              {ytIsLoadingInfo && (
                <div className="yt-loading-info">
                  <span>⏳ Video informatie laden...</span>
                </div>
              )}
              {/* Selected Video - Edit & Download */}
              {ytSelectedVideo && !ytIsLoadingInfo && (
                <div className="yt-selected-video">
                  <div className="yt-selected-header">
                    <img src={ytSelectedVideo.thumbnail} alt={ytSelectedVideo.title} className="yt-selected-thumb" />
                    <div className="yt-selected-info">
                      <div className="yt-selected-title">{ytSelectedVideo.title}</div>
                      <div className="yt-selected-channel">{ytSelectedVideo.channel}</div>
                    </div>
                    <button className="yt-close-btn" onClick={() => setYtSelectedVideo(null)}>×</button>
                  </div>
                  <div className="yt-edit-form">
                    <div className="yt-form-row">
                      <label>Titel</label>
                      <input
                        type="text"
                        value={ytEditTitle}
                        onChange={(e) => setYtEditTitle(e.target.value)}
                        disabled={ytIsDownloading}
                      />
                    </div>
                    <div className="yt-form-row">
                      <label>Artiest</label>
                      <input
                        type="text"
                        value={ytEditArtist}
                        onChange={(e) => setYtEditArtist(e.target.value)}
                        disabled={ytIsDownloading}
                      />
                    </div>
                    <div className="yt-form-row">
                      <label>Jaartal</label>
                      <input
                        type="number"
                        value={ytEditYear}
                        onChange={(e) => setYtEditYear(e.target.value)}
                        min="1900"
                        max={new Date().getFullYear()}
                        disabled={ytIsDownloading}
                      />
                    </div>
                  </div>
                  {!canAddToSelectedList && (
                    <p className="yt-hint">⚠️ Selecteer eerst een ronde of thema-avond om toe te voegen</p>
                  )}
                  <Button
                    variant="success"
                    size="large"
                    fullWidth
                    onClick={handleYouTubeDownload}
                    disabled={ytIsDownloading || !ytEditTitle || !ytEditArtist || !ytEditYear || !canAddToSelectedList}
                  >
                    {ytIsDownloading ? '⏳ Downloaden...' : `⬇️ Download & Toevoegen aan ${selectedListLabel}`}
                  </Button>
                </div>
              )}
            </div>
          )}
          {/* Downloads section */}
          {searchMode === 'downloads' && (
            <div className="downloads-section">
              <div className="downloads-header">
                <h3>📁 Gedownloade Bestanden</h3>
                <button 
                  className="refresh-btn" 
                  onClick={loadDownloadedFiles}
                  disabled={downloadsLoading}
                >
                  🔄 Verversen
                </button>
              </div>
              {downloadsLoading ? (
                <div className="downloads-loading">
                  <span className="spinner">⏳</span> Laden...
                </div>
              ) : downloadedFiles.length === 0 ? (
                <div className="empty-state">
                  <p>Geen gedownloade bestanden gevonden.</p>
                  <p className="hint">Download eerst nummers via YouTube om ze hier te zien.</p>
                </div>
              ) : (
                <div className="downloads-layout">
                  <div className="downloads-list">
                    {downloadedFiles.map((file) => (
                      <div 
                        key={file.filename} 
                        className={`download-item ${editingDownload?.filename === file.filename ? 'selected' : ''}`}
                        onClick={() => selectDownloadForEditing(file)}
                      >
                        <div className="download-info">
                          <div className="download-title">{file.title || file.filename}</div>
                          <div className="download-details">
                            {file.artist && <span>{file.artist}</span>}
                            {file.year && <span> • {file.year}</span>}
                            <span> • {(file.size / 1024 / 1024).toFixed(1)} MB</span>
                          </div>
                        </div>
                        <div className="download-actions">
                          <button
                            className={`play-btn ${playingDownloadUrl === file.url ? 'playing' : ''}`}
                            onClick={(e) => { e.stopPropagation(); playDownloadPreview(file); }}
                            title="Afspelen"
                          >
                            {playingDownloadUrl === file.url ? '⏹' : '▶'}
                          </button>
                          <button
                            className="add-btn"
                            onClick={(e) => { e.stopPropagation(); quickAddDownloadToRound(file); }}
                            title={canAddToSelectedList ? `Toevoegen aan ${selectedListLabel}` : 'Selecteer eerst een ronde of thema-avond'}
                            disabled={!canAddToSelectedList}
                          >
                            ➕
                          </button>
                          <button
                            className="delete-btn"
                            onClick={(e) => { e.stopPropagation(); deleteDownloadedFile(file.filename); }}
                            title="Verwijderen"
                          >
                            🗑️
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Edit form for selected download */}
                  {editingDownload && (
                    <div className="download-edit-form">
                      <h4>✏️ Bewerk & Toevoegen</h4>
                      <p className="hint">Pas de metadata aan voordat je toevoegt</p>
                      
                      <div className="edit-field">
                        <label>Titel</label>
                        <input
                          type="text"
                          value={dlEditTitle}
                          onChange={(e) => setDlEditTitle(e.target.value)}
                          placeholder="Titel van het nummer"
                        />
                      </div>
                      
                      <div className="edit-field">
                        <label>Artiest</label>
                        <input
                          type="text"
                          value={dlEditArtist}
                          onChange={(e) => setDlEditArtist(e.target.value)}
                          placeholder="Naam van de artiest"
                        />
                      </div>
                      
                      <div className="edit-field">
                        <label>Jaar</label>
                        <input
                          type="number"
                          value={dlEditYear}
                          onChange={(e) => setDlEditYear(e.target.value)}
                          min="1900"
                          max={new Date().getFullYear()}
                          placeholder="Jaar van uitgave"
                        />
                      </div>
                      {!canAddToSelectedList && (
                        <p className="warning">⚠️ Selecteer eerst een ronde of thema-avond aan de linkerkant</p>
                      )}
                      <Button
                        variant="success"
                        size="large"
                        fullWidth
                        onClick={addDownloadedFileToRound}
                        disabled={!dlEditTitle || !dlEditArtist || !dlEditYear || !canAddToSelectedList}
                      >
                        ➕ Toevoegen aan {selectedListLabel}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </div>
      {/* Event creation modal */}
      {showEventModal && (
        <div className="modal-overlay" onClick={() => {
          setShowEventModal(false);
          setNewEventIntermissionMessage('');
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>📅 Nieuw Evenement</h2>
            <div className="modal-field">
              <label>Naam</label>
              <input
                type="text"
                value={newEventName}
                onChange={(e) => setNewEventName(e.target.value)}
                placeholder="bijv. Hitsteravond 18 april"
                autoFocus
              />
            </div>
            <div className="modal-field">
              <label>Datum</label>
              <input
                type="date"
                value={newEventDate}
                onChange={(e) => setNewEventDate(e.target.value)}
              />
            </div>
            <div className="modal-field">
              <label>Beschrijving (optioneel)</label>
              <textarea
                value={newEventDescription}
                onChange={(e) => setNewEventDescription(e.target.value)}
                placeholder="Extra informatie..."
              />
            </div>
            <div className="modal-field">
              <label>Logo URL (optioneel)</label>
              <input
                type="url"
                value={newEventLogoUrl}
                onChange={(e) => setNewEventLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
              {newEventLogoUrl && (
                <div className="logo-preview">
                  <img src={newEventLogoUrl} alt="Logo preview" onError={(e) => (e.currentTarget.style.display = 'none')} />
                </div>
              )}
            </div>
            <div className="modal-field">
              <label>Pauzemelding op display (optioneel)</label>
              <textarea
                value={newEventIntermissionMessage}
                onChange={(e) => setNewEventIntermissionMessage(e.target.value)}
                placeholder="Bijv. Tijd voor een drankje aan de bar"
              />
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => {
                setShowEventModal(false);
                setNewEventIntermissionMessage('');
              }}>
                Annuleren
              </Button>
              <Button variant="success" onClick={createEvent} disabled={!newEventName.trim()}>
                ✓ Aanmaken
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Edit event settings modal */}
      {editingEvent && (
        <div className="modal-overlay" onClick={() => {
          setEditingEvent(null);
          setEditEventName('');
          setEditEventLogoUrl('');
          setEditEventIntermissionMessage('');
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>⚙️ Event Instellingen</h2>
            <p className="modal-subtitle">{editingEvent.name}</p>
            <div className="modal-field">
              <label>Evenementnaam</label>
              <input
                type="text"
                value={editEventName}
                onChange={(e) => setEditEventName(e.target.value)}
                placeholder="Bijv. Hitsteravond 2"
                autoFocus
              />
            </div>
            <div className="modal-field">
              <label>Logo URL</label>
              <input
                type="url"
                value={editEventLogoUrl}
                onChange={(e) => setEditEventLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
              />
              {editEventLogoUrl && (
                <div className="logo-preview">
                  <img src={editEventLogoUrl} alt="Logo preview" onError={(e) => (e.currentTarget.style.display = 'none')} />
                </div>
              )}
            </div>
            <div className="modal-field">
              <label>Pauzemelding op display</label>
              <textarea
                value={editEventIntermissionMessage}
                onChange={(e) => setEditEventIntermissionMessage(e.target.value)}
                placeholder="Bijv. Tijd voor een drankje aan de bar"
              />
            </div>
            <p className="timing-help">
              De evenementnaam wordt gebruikt in de backend en op het display. Als je een logo instelt, wordt dat logo op het display getoond in plaats van de teksttitel. De pauzemelding wordt gebruikt op het pauzescherm.
            </p>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => {
                setEditingEvent(null);
                setEditEventName('');
                setEditEventLogoUrl('');
                setEditEventIntermissionMessage('');
              }}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveEventLogo} disabled={!editEventName.trim()}>
                ✓ Opslaan
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Round creation modal */}
      {showRoundModal && (
        <div className="modal-overlay" onClick={() => setShowRoundModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>🎯 Nieuwe Ronde</h2>
            <div className="modal-field">
              <label>Naam</label>
              <input
                type="text"
                value={newRoundName}
                onChange={(e) => setNewRoundName(e.target.value)}
                placeholder="bijv. Ronde 1 - 80s Hits"
                autoFocus
              />
            </div>
            <div className="modal-field">
              <label>Spelvariant</label>
              <select
                value={newRoundVariant}
                onChange={(e) => setNewRoundVariant(e.target.value as HitsterRoundVariant)}
              >
                {HITSTER_ROUND_VARIANTS.map((variant) => (
                  <option key={variant.id} value={variant.id}>{variant.label}</option>
                ))}
              </select>
              <p className="timing-help">
                {HITSTER_ROUND_VARIANTS.find((variant) => variant.id === newRoundVariant)?.description}
              </p>
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={() => setShowRoundModal(false)}>
                Annuleren
              </Button>
              <Button variant="success" onClick={createRound} disabled={!newRoundName.trim()}>
                ✓ Aanmaken
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Lyrics editor modal */}
      {editingLyricsSongId && editingLyricsSong && (
        <div className="modal-overlay" onClick={closeLyricsEditor}>
          <div className="modal lyrics-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📝 Songtekst bewerken</h2>
            
            {/* Song info */}
            <div className="lyrics-song-info">
              <strong>{editingLyricsSong.title}</strong> - {editingLyricsSong.artist}
              <span className="lyrics-duration">
                Duur: {formatTime(editingLyricsSong.duration)}
              </span>
            </div>
            {/* Playback controls for sync */}
            <div className="lyrics-sync-controls">
              <button 
                className={`lyrics-play-btn ${lyricsIsPlaying ? 'playing' : ''}`}
                onClick={toggleLyricsPlayback}
                title={lyricsIsPlaying ? 'Pauzeren' : 'Afspelen'}
              >
                {lyricsIsPlaying ? '⏸' : '▶'}
              </button>
              <div className="lyrics-position">
                <span className="current-time">{formatTime(lyricsPlaybackPosition)}</span>
              </div>
              <input
                type="range"
                className="lyrics-seekbar"
                min={0}
                max={editingLyricsSong.duration || 1}
                value={lyricsPlaybackPosition}
                onChange={(e) => seekLyrics(parseInt(e.target.value))}
                title={`Spoel naar positie`}
              />
              <div className="lyrics-position">
                <span className="total-time">{formatTime(editingLyricsSong.duration)}</span>
              </div>
              <button 
                className="insert-timestamp-btn"
                onClick={insertTimestamp}
                title="Voeg tijdstip in op cursor positie (of druk Spatie)"
              >
                ⏱ Tijdstip invoegen
              </button>
              <button
                className={`lyrics-preview-toggle-btn ${lyricsPreviewMode ? 'active' : ''}`}
                onClick={() => setLyricsPreviewMode(!lyricsPreviewMode)}
                title="Live preview van synchronisatie"
              >
                {lyricsPreviewMode ? '✏️ Bewerken' : '👁 Preview'}
              </button>
            </div>
            {/* Shift all timestamps */}
            <div className="lyrics-offset-shift">
              <span>Verschuif alle:</span>
              <button onClick={() => shiftAllTimestamps(-1)} title="1 seconde eerder">-1s</button>
              <button onClick={() => shiftAllTimestamps(-0.5)} title="0.5 seconde eerder">-½s</button>
              <button onClick={() => shiftAllTimestamps(0.5)} title="0.5 seconde later">+½s</button>
              <button onClick={() => shiftAllTimestamps(1)} title="1 seconde later">+1s</button>
            </div>
            <p className="lyrics-help">
              Speel het nummer af en druk op <kbd>Spatie</kbd> (of klik "Tijdstip invoegen") om timestamps toe te voegen. De cursor springt automatisch naar de volgende regel.<br/>
              Format: <strong>[m:ss]</strong> gevolgd door de tekst.
            </p>
            <div className="lyrics-auto-search">
              <button 
                className="search-lyrics-auto-btn"
                onClick={searchLyricsAuto}
                disabled={lyricsSearching}
              >
                {lyricsSearching ? '⏳ Zoeken...' : '🔍 Zoek songtekst automatisch'}
              </button>
            </div>
            <div className="modal-field">
              {lyricsPreviewMode ? (
                <div className="lyrics-preview-panel">
                  {(() => {
                    const lines = parseLyricsForPreview(lyricsText);
                    let activeIdx = -1;
                    for (let i = 0; i < lines.length; i++) {
                      if (lines[i].timestamp <= lyricsPlaybackPosition) activeIdx = i;
                      else break;
                    }
                    return lines.length === 0 ? (
                      <div className="lyrics-preview-empty">Geen timestamps gevonden. Voeg eerst timestamps toe in de bewerkingsmodus.</div>
                    ) : lines.map((line, i) => (
                      <div
                        key={i}
                        ref={i === activeIdx ? activeLyricsLineRef : undefined}
                        className={`preview-line${i === activeIdx ? ' active' : i < activeIdx ? ' past' : ''}`}
                      >
                        <span className="preview-timestamp">[{formatTime(line.timestamp)}]</span>
                        <span>{line.text}</span>
                      </div>
                    ));
                  })()}
                </div>
              ) : (
                <textarea
                  ref={lyricsTextareaRef}
                  value={lyricsText}
                  onChange={(e) => setLyricsText(e.target.value)}
                  placeholder="[0:00] Eerste regel van het nummer
[0:15] Tweede regel komt na 15 seconden
[0:30] Derde regel na 30 seconden
[1:00] Na een minuut..."
                  rows={15}
                  autoFocus
                />
              )}
            </div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={closeLyricsEditor}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveLyrics}>
                {lyricsSaved ? '✓ Opgeslagen!' : '✓ Opslaan'}
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* YouTube Timing Editor Modal */}
      {editingYoutubeTimingSongId && editingYoutubeTimingSong && (
        <div className="modal-overlay" onClick={closeYoutubeTimingEditor}>
          <div className="modal youtube-timing-modal" onClick={(e) => e.stopPropagation()}>
            <h2>⚙️ Nummer Instellingen</h2>
            
            <div className="timing-song-info">
              <strong>{editingYoutubeTimingSong.title}</strong> - {editingYoutubeTimingSong.artist}
            </div>
            
            {/* Volume Control */}
            <div className="volume-control-section">
              <h3>🔊 Volume</h3>
              <p className="timing-help">
                Pas het volume aan zodat alle nummers even hard klinken.
              </p>
              <div className="volume-slider-container">
                <input 
                  type="range" 
                  min="10" 
                  max="200" 
                  value={volumeGainInput}
                  onChange={(e) => setVolumeGainInput(parseInt(e.target.value))}
                  className="volume-slider"
                />
                <span className="volume-value">{volumeGainInput}%</span>
              </div>
            </div>
            
            {/* YouTube Fragment Control - only show for YouTube songs */}
            {editingYoutubeTimingSong.youtubeVideoId && (
              <>
                <h3>⏱️ YouTube Fragment</h3>
                <p className="timing-help">
                  Klik op de video om naar een positie te gaan, dan klik op "📍 Start" of "📍 Eind" om de tijd over te nemen.
                </p>
                
                <div className="timing-preview">
                  <div id="yt-timing-player" style={{ width: '100%', height: '200px', borderRadius: '8px' }}></div>
                </div>
                
                <div className="timing-inputs">
                  <div className="timing-field">
                    <label>Starttijd (m:ss)</label>
                    <div className="timing-input-row">
                      <input 
                        type="text" 
                        value={youtubeStartTimeInput}
                        onChange={(e) => setYoutubeStartTimeInput(e.target.value)}
                        placeholder="0:00"
                      />
                      <button 
                        type="button" 
                        className="grab-time-btn"
                        onClick={grabYoutubeStartTime}
                        title="Pak huidige positie als starttijd"
                      >
                        📍 Start
                      </button>
                    </div>
                  </div>
                  <div className="timing-field">
                    <label>Eindtijd (m:ss)</label>
                    <div className="timing-input-row">
                      <input 
                        type="text" 
                        value={youtubeEndTimeInput}
                        onChange={(e) => setYoutubeEndTimeInput(e.target.value)}
                        placeholder="bijv. 0:30"
                      />
                      <button 
                        type="button" 
                        className="grab-time-btn"
                        onClick={grabYoutubeEndTime}
                        title="Pak huidige positie als eindtijd"
                      >
                        📍 Eind
                      </button>
                    </div>
                  </div>
                </div>
                
                <div className="timing-preview-btn">
                  <button 
                    type="button" 
                    className="preview-fragment-btn"
                    onClick={previewYoutubeFragment}
                    disabled={!youtubeStartTimeInput}
                  >
                    ▶️ Preview vanaf start
                  </button>
                </div>
              </>
            )}

            {/* Audio Start Time - for all non-YouTube songs */}
            {!editingYoutubeTimingSong.youtubeVideoId && (
              <>
                <h3>⏱️ Audio Starttijd</h3>
                <p className="timing-help">
                  Laat het nummer starten op een bepaald tijdstip (bijv. <strong>0:30</strong> om 30 seconden in te beginnen).
                </p>
                <div className="timing-inputs">
                  <div className="timing-field">
                    <label>Starttijd (m:ss)</label>
                    <input
                      type="text"
                      value={audioStartTimeInput}
                      onChange={(e) => setAudioStartTimeInput(e.target.value)}
                      placeholder="0:00"
                    />
                  </div>
                </div>
              </>
            )}
            
            <div className="modal-actions">
              <Button variant="secondary" onClick={closeYoutubeTimingEditor}>
                Annuleren
              </Button>
              <Button variant="success" onClick={saveYoutubeTiming}>
                ✓ Opslaan
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* QR Scanner Modal */}
      {showQrScanner && (
        <div className="modal-overlay" onClick={stopQrScanner}>
          <div className="qr-scanner-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>📷 QR Code Scannen</h2>
              <button className="close-btn" onClick={stopQrScanner}>✕</button>
            </div>
            <p className="qr-instructions">
              Scan een Hitster QR code om het nummer direct toe te voegen aan <strong>{selectedRound?.name}</strong>
            </p>
            {qrProcessing && (
              <div className="qr-processing">
                <span className="spinner">⏳</span> Verwerken...
              </div>
            )}
            {qrScannerError && (
              <div className="qr-error">{qrScannerError}</div>
            )}
            <div id="qr-reader"></div>
            <div className="modal-actions">
              <Button variant="secondary" onClick={stopQrScanner}>
                Sluiten
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* Playlist Wizard Modal */}
      {showPlaylistWizard && (selectedRound || (selectedEvent && getEventType(selectedEvent) === 'theme-night')) && (
        <PlaylistWizard
          onClose={() => setShowPlaylistWizard(false)}
          onAddSongs={(songs) => {
            const baseCount = selectedRound
              ? selectedRound.songs.length
              : ((selectedEvent?.themeSongs || []).length);

            // Add songs to the current round or theme-night list with all Spotify/YouTube data
            const newSongs: EventSong[] = songs.map((song, index) => ({
              id: `song-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
              title: song.title,
              artist: song.artist,
              album: song.album,
              year: song.year || new Date().getFullYear(),
              duration: song.duration || 0,
              order: baseCount + index + 1,
              spotifyId: song.spotifyId,
              spotifyUri: song.spotifyUri,
              previewUrl: song.previewUrl,
              imageUrl: song.imageUrl,
              youtubeVideoId: song.youtubeVideoId,
            }));

            if (selectedRound) {
              addMultipleSongsToRound(newSongs);
            } else {
              addMultipleSongsToThemeNight(newSongs);
            }

            setShowPlaylistWizard(false);
          }}
        />
      )}
      {/* Song Overview Modal - Fullscreen Table */}
      {showSongOverview && selectedEvent && (selectedRound || isThemeNightSelected) && (
        <div className="modal-overlay fullscreen" onClick={() => setShowSongOverview(false)}>
          <div className="song-overview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="overview-header">
              {selectedRound && (
                <div className="overview-nav">
                  <button 
                    className="nav-btn"
                    disabled={selectedEvent.rounds.findIndex(r => r.id === selectedRound.id) === 0}
                    onClick={() => {
                      const currentIndex = selectedEvent.rounds.findIndex(r => r.id === selectedRound.id);
                      if (currentIndex > 0) {
                        setSelectedRound(selectedEvent.rounds[currentIndex - 1]);
                      }
                    }}
                  >
                    ← Vorige
                  </button>
                  <h2>🎵 {selectedRound.name}</h2>
                  <button 
                    className="nav-btn"
                    disabled={selectedEvent.rounds.findIndex(r => r.id === selectedRound.id) === selectedEvent.rounds.length - 1}
                    onClick={() => {
                      const currentIndex = selectedEvent.rounds.findIndex(r => r.id === selectedRound.id);
                      if (currentIndex < selectedEvent.rounds.length - 1) {
                        setSelectedRound(selectedEvent.rounds[currentIndex + 1]);
                      }
                    }}
                  >
                    Volgende →
                  </button>
                </div>
              )}
              {isThemeNightSelected && (
                <h2>🎵 Thema-avond</h2>
              )}
              <span className="overview-count">
                {selectedRound && isBattleGenerationsRound && overviewGenerationFilter !== 'all'
                  ? `${overviewSongs.length} / ${selectedRound.songs.length} nummers`
                  : `${(selectedRound?.songs.length || selectedEvent.themeSongs?.length || 0)} nummers`}
              </span>
              <div className="overview-controls">
                <div className="fade-duration-control compact">
                  <label htmlFor="fade-duration-overview">Fade</label>
                  <input
                    id="fade-duration-overview"
                    type="range"
                    min={1}
                    max={10}
                    step={0.5}
                    value={fadeDurationSeconds}
                    onChange={(e) => handleFadeDurationChange(parseFloat(e.target.value))}
                  />
                  <span>{fadeDurationLabel}</span>
                </div>
                <button 
                  className="fade-btn"
                  onClick={triggerFadeOut}
                  disabled={isFading || !liveSongId}
                  title="Fade out en stop"
                >
                  {isFading ? '🔉 Fading...' : '🔊➡️🔇 Fade'}
                </button>
                <button 
                  className="stop-display-btn"
                  onClick={stopDisplay}
                  disabled={!liveSongId}
                  title="Stop display"
                >
                  ⏹ Stop Display
                </button>
              </div>
              <button className="close-overview-btn" onClick={() => {
                if (overviewAudioRef.current) {
                  overviewAudioRef.current.pause();
                  overviewAudioRef.current = null;
                }
                setOverviewPlayingSongId(null);
                setShowSongOverview(false);
              }}>✕</button>
            </div>
            
            {/* Overview Tabs */}
            <div className="overview-tabs">
              <button 
                className={`overview-tab ${overviewTab === 'table' ? 'active' : ''}`}
                onClick={() => setOverviewTab('table')}
              >
                📋 Overzicht
              </button>
              <button 
                className={`overview-tab ${overviewTab === 'discogs' ? 'active' : ''}`}
                onClick={() => setOverviewTab('discogs')}
              >
                💿 Discogs
              </button>
              <button 
                className={`overview-tab ${overviewTab === 'tools' ? 'active' : ''}`}
                onClick={() => setOverviewTab('tools')}
              >
                🛠️ Tools
              </button>
            </div>

            {selectedRound && isBattleGenerationsRound && overviewTab === 'table' && selectedRound.songs.length > 0 && (
              <div className="overview-generation-filters">
                {GENERATION_FILTER_OPTIONS.map((filter) => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`overview-generation-filter-btn ${overviewGenerationFilter === filter.id ? 'active' : ''}`}
                    onClick={() => setOverviewGenerationFilter(filter.id)}
                  >
                    {filter.label}
                  </button>
                ))}
                <span className="overview-generation-filter-count">{overviewSongs.length} / {selectedRound.songs.length}</span>
              </div>
            )}
            {isThemeNightSelected && overviewTab === 'table' && (
              <div className="overview-generation-filters">
                <button
                  type="button"
                  className={`overview-generation-filter-btn ${themeNightOverviewSetFilter === 'all' ? 'active' : ''}`}
                  onClick={() => setThemeNightOverviewSetFilter('all')}
                >
                  Alle sets
                </button>
                {themeNightSetNumbers.map((setNumber) => (
                  <button
                    key={setNumber}
                    type="button"
                    className={`overview-generation-filter-btn ${themeNightOverviewSetFilter === String(setNumber) ? 'active' : ''}`}
                    onClick={() => setThemeNightOverviewSetFilter(String(setNumber))}
                  >
                    Set {setNumber}
                  </button>
                ))}
                <span className="overview-generation-filter-count">{filteredOverviewSongs.length} / {overviewSongs.length}</span>
              </div>
            )}
            
            <div className="overview-content">
              {/* Table View */}
              {overviewTab === 'table' && (
                <div className="overview-table-container">
                  <table className="overview-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Titel</th>
                        <th>Artiest</th>
                        <th>Jaar</th>
                        <th>Album</th>
                        <th>Bron</th>
                        <th>Set</th>
                        <th>Acties</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOverviewSongs.length === 0 && (
                        <tr>
                          <td colSpan={8} className="overview-empty-row">Geen nummers gevonden voor deze filter.</td>
                        </tr>
                      )}
                      {filteredOverviewSongs.map((song, index) => (
                        <tr
                          key={song.id}
                          className={`${overviewPlayingSongId === song.id ? 'playing' : ''} ${armedDisplaySongId === song.id ? 'armed' : ''} ${editingOverviewSongId === song.id ? 'editing' : ''} ${draggedSongId === song.id ? 'dragging' : ''} ${dropTargetSongId === song.id ? 'drop-target' : ''}`}
                          draggable={editingOverviewSongId !== song.id}
                          onDragStart={(event) => handleSongDragStart(song.id, event)}
                          onDragOver={(event) => handleSongDragOver(song.id, event)}
                          onDrop={(event) => void handleSongDrop(song.id, event)}
                          onDragEnd={handleSongDragEnd}
                        >
                          <td className="song-num\">
                            {overviewRoundContext && (
                              <button
                                type="button"
                                className={`song-num-button ${armedDisplaySongId === song.id ? 'armed' : ''}`}
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  void armSongForDisplay(song, overviewRoundContext, overviewRoundContext.order - 1);
                                }}
                                title="Zet dit nummer klaar als volgende scan op het display"
                              >
                                {index + 1}
                              </button>
                            )}
                            {!overviewRoundContext && (
                              <span className="song-num-label">{index + 1}</span>
                            )}
                          </td>
                          <td 
                            className="song-title-cell editable"
                            onClick={() => {
                              if (editingOverviewSongId !== song.id) {
                                setEditingOverviewSongId(song.id);
                                setEditOverviewTitle(song.title);
                                setEditOverviewArtist(song.artist);
                                setEditOverviewYear(song.year.toString());
                                setEditOverviewYearSource(song.yearSource || '');
                              }
                            }}
                          >
                            {editingOverviewSongId === song.id ? (
                              <input
                                type="text"
                                value={editOverviewTitle}
                                onChange={(e) => setEditOverviewTitle(e.target.value)}
                                className="edit-input"
                                onClick={(e) => e.stopPropagation()}
                                autoFocus
                              />
                            ) : song.title}
                          </td>
                          <td 
                            className="song-artist-cell editable"
                            onClick={() => {
                              if (editingOverviewSongId !== song.id) {
                                setEditingOverviewSongId(song.id);
                                setEditOverviewTitle(song.title);
                                setEditOverviewArtist(song.artist);
                                setEditOverviewYear(song.year.toString());
                                setEditOverviewYearSource(song.yearSource || '');
                              }
                            }}
                          >
                            {editingOverviewSongId === song.id ? (
                              <input
                                type="text"
                                value={editOverviewArtist}
                                onChange={(e) => setEditOverviewArtist(e.target.value)}
                                className="edit-input"
                                onClick={(e) => e.stopPropagation()}
                              />
                            ) : song.artist}
                          </td>
                          <td 
                            className="song-year-cell editable"
                            onClick={() => {
                              if (editingOverviewSongId !== song.id) {
                                setEditingOverviewSongId(song.id);
                                setEditOverviewTitle(song.title);
                                setEditOverviewArtist(song.artist);
                                setEditOverviewYear(song.year.toString());
                                setEditOverviewYearSource(song.yearSource || '');
                              }
                            }}
                          >
                            {editingOverviewSongId === song.id ? (
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                                <input
                                  type="number"
                                  value={editOverviewYear}
                                  onChange={(e) => setEditOverviewYear(e.target.value)}
                                  className="edit-input year-input"
                                  min="1900"
                                  max={new Date().getFullYear()}
                                  onClick={(e) => e.stopPropagation()}
                                />
                                <input
                                  type="text"
                                  value={editOverviewYearSource}
                                  onChange={(e) => setEditOverviewYearSource(e.target.value)}
                                  className="edit-input"
                                  placeholder="🔗 Bron (URL of tekst)"
                                  onClick={(e) => e.stopPropagation()}
                                  style={{ fontSize: '0.8em', minWidth: 120 }}
                                />
                              </div>
                            ) : (
                              <span title={song.yearSource || undefined}>
                                {song.year}{song.yearSource ? ' 🔗' : ''}
                              </span>
                            )}
                          </td>
                          <td className="song-album-cell">{song.album || '-'}</td>
                          <td className="song-source-cell">
                            <span className={`song-source-badge ${song.spotifyUri ? 'spotify' : song.youtubeVideoId ? 'youtube' : song.audioUrl ? 'file' : 'unknown'}`}>
                              <span className="song-source-dot" aria-hidden="true"></span>
                              <span>
                                {song.spotifyUri ? 'Spotify' : song.youtubeVideoId ? 'YouTube' : song.audioUrl ? 'Bestand' : '-'}
                              </span>
                            </span>
                          </td>
                          <td className="song-set-cell">{getSongSetLabel(song.id)}</td>
                          <td className="song-actions-cell">
                            <div className="song-actions-inner">
                            {editingOverviewSongId === song.id ? (
                              <>
                                <button 
                                  className="action-btn save"
                                  onClick={() => {
                                    if (editOverviewTitle.trim() && editOverviewArtist.trim() && editOverviewYear) {
                                      updateSongFromDiscogs(song.id, {
                                        title: editOverviewTitle.trim(),
                                        artist: editOverviewArtist.trim(),
                                        year: parseInt(editOverviewYear),
                                        yearSource: editOverviewYearSource.trim() || undefined
                                      });
                                      setEditingOverviewSongId(null);
                                    }
                                  }}
                                  title="Opslaan"
                                >
                                  ✓
                                </button>
                                <button 
                                  className="action-btn cancel"
                                  onClick={() => setEditingOverviewSongId(null)}
                                  title="Annuleren"
                                >
                                  ✕
                                </button>
                              </>
                            ) : (
                              <>
                                <button 
                                  className={`action-btn play ${overviewPlayingSongId === song.id ? 'playing' : ''}`}
                                  onClick={() => {
                                    if (overviewPlayingSongId === song.id) {
                                      if (overviewAudioRef.current) {
                                        overviewAudioRef.current.pause();
                                        overviewAudioRef.current = null;
                                      }
                                      setOverviewPlayingSongId(null);
                                    } else {
                                      if (overviewAudioRef.current) {
                                        overviewAudioRef.current.pause();
                                      }
                                      const audioUrl = song.previewUrl || song.audioUrl;
                                      if (audioUrl) {
                                        const audio = new Audio(audioUrl);
                                        overviewAudioRef.current = audio;
                                        audio.play();
                                        setOverviewPlayingSongId(song.id);
                                        audio.onended = () => {
                                          setOverviewPlayingSongId(null);
                                          overviewAudioRef.current = null;
                                        };
                                      } else if (song.spotifyUri && isAuthenticated && isPremium && isReady) {
                                        play(song.spotifyUri);
                                        setOverviewPlayingSongId(song.id);
                                      }
                                    }
                                  }}
                                  title={overviewPlayingSongId === song.id ? 'Pauzeren' : 'Afspelen'}
                                >
                                  {overviewPlayingSongId === song.id ? '⏸️' : '▶️'}
                                </button>
                                {overviewRoundContext && (
                                  <button 
                                    className="action-btn display"
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                    }}
                                    onClick={(e) => {
                                      e.preventDefault();
                                      e.stopPropagation();
                                      void playOnDisplay(song, overviewRoundContext, overviewRoundContext.order - 1);
                                    }}
                                    disabled={startingDisplaySongId === song.id}
                                    title={isThemeNightSelected
                                      ? `Afspelen op Display (Set ${activeThemeNightSetNumber}, plek ${activeThemeNightSetSlot})`
                                      : 'Afspelen op Display'}
                                  >
                                    📺
                                  </button>
                                )}
                                {isThemeNightSelected && (
                                  <button
                                    className="action-btn set"
                                    onClick={() => void addSongToManualThemeNightSet(song)}
                                    title="Handmatig toevoegen aan set 1,2,3..."
                                  >
                                    ➕
                                  </button>
                                )}
                                <button 
                                  className={`action-btn lyrics ${song.lyrics ? 'has-lyrics' : ''}`}
                                  onClick={() => openLyricsEditor(song)}
                                  title={song.lyrics ? 'Songtekst bewerken' : 'Songtekst toevoegen'}
                                >
                                  📝
                                </button>
                              </>
                            )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              
              {/* Discogs View */}
              {overviewTab === 'discogs' && overviewRoundContext && overviewRoundContext.songs.length > 0 && (
                <div className="overview-discogs-container">
                  <DiscogsVerify 
                    songs={overviewRoundContext.songs} 
                    onUpdateSong={updateSongFromDiscogs}
                  />
                </div>
              )}
              
              {/* Tools View */}
              {overviewTab === 'tools' && overviewRoundContext && overviewRoundContext.songs.length > 0 && (
                <div className="overview-tools-container">
                  <SongTools 
                    songs={overviewRoundContext.songs} 
                    onUpdateSong={updateSongFromDiscogs}
                    onRemoveSong={removeSongFromRound}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showSpotifyResultsModal && (
        <div className="modal-overlay" onClick={() => setShowSpotifyResultsModal(false)}>
          <div className="modal spotify-results-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Spotify resultaten ({searchResults.length})</h2>
              <button
                className="modal-close-btn"
                onClick={() => setShowSpotifyResultsModal(false)}
                title="Sluiten"
              >
                ×
              </button>
            </div>
            <div className="search-results search-results-modal-list">
              {searchResults.map(track => {
                const isAdded = selectedSongList.some(s => s.spotifyId === track.id);
                return (
                  <div key={track.id} className={`track-card ${isAdded ? 'added' : ''}`}>
                    <div className="track-image">
                      {track.thumbnail ? (
                        <img src={track.thumbnail} alt={track.title} />
                      ) : (
                        <div className="no-image">🎵</div>
                      )}
                    </div>
                    <div className="track-info">
                      <div className="track-title">{track.title}</div>
                      <div className="track-artist">{track.artist}</div>
                      <div className="track-meta">
                        <span className="track-album">{track.album}</span>
                        {track.year && <span className="track-year">{track.year}</span>}
                        <span className="track-duration">{formatDuration(track.duration)}</span>
                        {track.previewUrl && <span className="has-preview">🎧</span>}
                      </div>
                    </div>
                    <div className="track-actions">
                      <button
                        className="lyrics-btn"
                        onClick={() => setLyricsTrack({ artist: track.artist, title: track.title })}
                        title="Zoek songtekst"
                      >
                        🎤
                      </button>
                      {isAuthenticated && isPremium && isReady && (
                        <SpotifyPlayButton
                          trackUri={`spotify:track:${track.id}`}
                          trackName={track.title}
                        />
                      )}
                      {!isAdded && canAddToSelectedList ? (
                        <button
                          className="add-to-round-btn"
                          onClick={() => addSongToRound(track)}
                        >
                          ➕ Toevoegen
                        </button>
                      ) : isAdded ? (
                        <span className="added-label">✓ Toegevoegd</span>
                      ) : (
                        <span className="no-round-hint">Selecteer ronde of thema-avond</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
      
      {/* Lyrics Modal */}
      {lyricsTrack && (
        <LyricsModal
          artist={lyricsTrack.artist}
          title={lyricsTrack.title}
          onClose={() => setLyricsTrack(null)}
        />
      )}

      {/* ── Playback Log ── */}
      <div className={`playback-log-panel ${showPlaybackLog ? 'open' : ''}`}>
        <button
          className="playback-log-toggle"
          onClick={() => setShowPlaybackLog(v => !v)}
          title="Afspeel-logboek"
        >
          📋 {showPlaybackLog ? 'Sluit log' : 'Log'}
          {!showPlaybackLog && playbackLogEntries.length > 0 && (
            <span className="log-badge">{playbackLogEntries.length}</span>
          )}
        </button>
        {showPlaybackLog && (
          <div className="playback-log-body">
            <div className="playback-log-header">
              <span>Afspeel-logboek</span>
              <button className="log-clear-btn" onClick={clearPlaybackLog}>Wissen</button>
            </div>
            <div className="playback-log-entries">
              {playbackLogEntries.length === 0 && (
                <div className="log-empty">Nog geen logregels. Start een nummer om te beginnen.</div>
              )}
              {playbackLogEntries.map(entry => (
                <div key={entry.id} className={`log-entry log-${entry.level} log-src-${entry.source}`}>
                  <span className="log-time">{new Date(entry.ts).toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                  <span className="log-source">{entry.source === 'dashboard' ? '🎛️' : '📺'}</span>
                  <span className="log-event">{entry.event}</span>
                  {entry.data && (
                    <details className="log-details">
                      <summary>details</summary>
                      <pre>{JSON.stringify(entry.data, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Theme-night inline answers/teams panel */}
      {themeNightPanelSetId && selectedEvent && (
        <div className="tn-panel-overlay" onClick={() => setThemeNightPanelSetId(null)}>
          <div className="tn-panel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="tn-panel-header">
              <h2>
                {(() => {
                  const setNumber = getThemeNightSetNumberFromId(themeNightPanelSetId);
                  return setNumber ? `Set ${setNumber}` : themeNightPanelSetId;
                })()}
              </h2>
              <button className="tn-panel-close" onClick={() => setThemeNightPanelSetId(null)}>✕</button>
            </div>
            <div className="tn-panel-tabs">
              <button
                className={`tn-panel-tab${themeNightPanelFocus === 'answers' ? ' active' : ''}`}
                onClick={() => setThemeNightPanelFocus('answers')}
              >
                📋 Antwoorden
              </button>
              <button
                className={`tn-panel-tab${themeNightPanelFocus === 'teams' ? ' active' : ''}`}
                onClick={() => setThemeNightPanelFocus('teams')}
              >
                👥 Teams
              </button>
            </div>
            <div className="tn-panel-body">
              {themeNightPanelLoading ? (
                <p className="tn-panel-loading">Laden...</p>
              ) : themeNightPanelFocus === 'answers' ? (
                themeNightPanelAnswerKeys.length === 0 ? (
                  <p className="tn-panel-empty">Nog geen nummers gespeeld in deze set.</p>
                ) : (
                  <table className="tn-answer-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Artiest</th>
                        <th>Titel</th>
                        <th>Jaar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {themeNightPanelAnswerKeys.map((entry, index) => {
                        const song = (selectedEvent.themeSongs || []).find((s) => s.id === entry.songId);
                        return (
                          <tr key={entry.songId}>
                            <td>{index + 1}</td>
                            <td>{song?.artist ?? entry.songId}</td>
                            <td>{song?.title ?? '—'}</td>
                            <td>{song?.year ?? '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )
              ) : (
                // Teams tab
                !selectedEvent.teams || selectedEvent.teams.length === 0 ? (
                  <p className="tn-panel-empty">Geen teams gevonden. Voeg teams toe aan het evenement.</p>
                ) : (
                  <div className="tn-teams-grid">
                    {selectedEvent.teams.map((team) => (
                      <div key={team.id} className="tn-team-card">
                        <div className="tn-team-name">{team.name}</div>
                        <div className="tn-team-score">{themeNightPanelBingoScores[team.id] || 0}</div>
                        <div className="tn-team-btns">
                          <button
                            className="tn-bingo-btn"
                            onClick={async () => {
                              const newScore = (themeNightPanelBingoScores[team.id] || 0) + 1;
                              setThemeNightPanelBingoScores((prev) => ({ ...prev, [team.id]: newScore }));
                              try {
                                await fetch(`${API_BASE}/api/bingo/score`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ eventId: selectedEvent.id, roundId: themeNightPanelSetId, teamId: team.id, action: 'set', value: newScore }),
                                });
                              } catch { /* ignore */ }
                            }}
                          >
                            +1
                          </button>
                          <button
                            className="tn-bingo-btn minus"
                            disabled={(themeNightPanelBingoScores[team.id] || 0) <= 0}
                            onClick={async () => {
                              const newScore = Math.max(0, (themeNightPanelBingoScores[team.id] || 0) - 1);
                              setThemeNightPanelBingoScores((prev) => ({ ...prev, [team.id]: newScore }));
                              try {
                                await fetch(`${API_BASE}/api/bingo/score`, {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  body: JSON.stringify({ eventId: selectedEvent.id, roundId: themeNightPanelSetId, teamId: team.id, action: 'set', value: newScore }),
                                });
                              } catch { /* ignore */ }
                            }}
                          >
                            -1
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default SpotifyDashboard;
